<template>
    <div v-if="order" class="order-details">
        <v-snackbar v-model="snackbar.show" color="error">
            {{ snackbar.message }}
        </v-snackbar>
        <div
            style="gap: 10px"
            class="order-details__header show-scroll-up-mobile d-flex flex-row justify-space-between py-4"
        >
            <div class="pb-0 flex-grow-1">
                <div style="gap: 10px" class="d-flex flex-row flex-nowrap flex-grow-1 align-center">
                    <v-btn
                        v-if="isMobile"
                        variant="text"
                        :to="{name: 'orders'}"
                        size="small"
                        class="px-0"
                        data-cy="back-to-orders"
                    >
                        <v-icon class="text-primary">mdi-arrow-left</v-icon>
                    </v-btn>
                    <div
                        class="order-details__header-title-wrapper d-flex align-center flex-grow-1 flex-wrap text-subtitle-1 text-sm-h6"
                    >
                        <div
                            class="order-details__title font-weight-bold text-nowrap"
                            :data-cy="`reservation-number-${order.orderId}`"
                        >
                            {{ `${$t('order')} #${order.orderId}` }}
                        </div>
                        <div v-if="order.parentOrderId">
                            <v-icon class="mx-1" color="grey-lighten-1" size="small">mdi-arrow-right-bold</v-icon>
                            <div class="font-weight-bold text-grey-lighten-1">
                                {{ ` #${order.parentOrderId}` }}
                            </div>
                        </div>
                        <OrderDetailsTitleEditor />
                        <OrderDetailsReservationStatusEditor v-if="orderStatus && isShowOrderStatus" />
                    </div>
                </div>
            </div>
            <div class="pb-0">
                <v-row no-gutters style="gap: 10px" class="justify-end align-center flex-nowrap">
                    <template v-if="isShowSettingsBtn">
                        <v-btn
                            v-if="!isMobile"
                            variant="outlined"
                            prepend-icon="mdi-cog"
                            class="text-none"
                            data-cy="show-settings"
                            color="inherit"
                            @click="showSettingsModal = true"
                        >
                            {{ $t('display_settings') }}
                        </v-btn>
                        <v-btn v-else variant="text" icon="mdi-cog" class="ms-auto" @click="showSettingsModal = true" />
                        <OrderDetailsSettingsModal v-model="showSettingsModal" />
                    </template>
                    <OrderDetailsManageReservation
                        v-if="showMoveToArchive || isTO1"
                        :show-move-to-archive="showMoveToArchive"
                    />
                </v-row>
            </div>
        </div>
        <v-row>
            <v-col v-if="!isMobile" cols="12" class="pt-4">
                <v-row no-gutters style="gap: 10px">
                    <order-destination-select v-if="isTO1 && $conf.public.account.destinationInReservation" />
                    <CampaignSelect v-if="isTO1 && (personRole === 'director' || personRole === 'supervisor')" />
                    <ClassificationSelect v-if="isTO1" />
                </v-row>
            </v-col>
            <v-col
                cols="12"
                md="9"
                class="order-details__header-controls pb-0 d-sm-flex justify-space-between flex-wrap"
            >
                <div class="px-md-2">
                    <v-tabs v-model="selectedTabIndex" stacked fixed-tabs show-arrows center-active>
                        <v-tab
                            v-for="(tab, i) in tabs"
                            :key="`${i}-${tab.name}`"
                            :disabled="getTabDisabled(tab)"
                            :style="isMobile ? 'min-width: 60px; padding: 0 6px' : ''"
                            :data-cy="`${tab.name}-tab`"
                        >
                            <v-badge
                                v-if="tab.name === 'communications' && communicationsWithoutInternalNotes.length"
                                color="green"
                                :content="`${
                                    communicationsWithoutInternalNotes.filter(i => i.status === 'ACTIVE').length
                                }`"
                                class="badge"
                                floating
                            >
                                <div class="d-flex flex-column align-center full-height">
                                    <v-icon class="mb-1">{{ tab.icon }}</v-icon>
                                    <span style="font-size: 8px; margin-top: 2px">
                                        {{ $t(tab.label) }}
                                    </span>
                                </div>
                            </v-badge>
                            <v-badge
                                v-else-if="tab.name === 'internalNotes'"
                                color="green"
                                :content="`${internalNotes.filter(i => i.status === 'ACTIVE').length}`"
                                class="badge"
                                floating
                            >
                                <div class="d-flex flex-column align-center full-height">
                                    <v-icon class="mb-1">{{ tab.icon }}</v-icon>
                                    <span style="font-size: 8px; margin-top: 2px">
                                        {{ $t(tab.label) }}
                                    </span>
                                </div>
                            </v-badge>
                            <v-badge
                                v-else-if="tab.name === 'attachments' && documents.length"
                                color="green"
                                :content="documents.length"
                                class="badge"
                                floating
                            >
                                <div class="d-flex flex-column align-center full-height">
                                    <v-icon class="mb-1">{{ tab.icon }}</v-icon>
                                    <span style="font-size: 8px; margin-top: 2px">
                                        {{ $t(tab.label) }}
                                    </span>
                                </div>
                            </v-badge>
                            <template v-else>
                                <div class="d-flex flex-column align-center full-height">
                                    <v-icon class="mb-1">{{ tab.icon }}</v-icon>
                                    <span style="font-size: 8px; margin-top: 2px">
                                        {{ $t(tab.label) }}
                                    </span>
                                </div>
                            </template>
                        </v-tab>
                        <chat-modal v-if="order && $conf.public.isChatEnabled" :order-id="order.orderId" />
                    </v-tabs>
                </div>
                <v-expand-transition>
                    <div class="display-format d-flex flex-wrap align-center">
                        <v-divider v-if="isMobile" />
                        <div
                            v-if="tabSelectedName === 'info'"
                            class="display-format__btns d-flex flex-nowrap align-center"
                            :class="{'justify-space-between flex-grow-1': isMobile}"
                        >
                            <span class="mr-2 display-format-label">
                                {{ $t('display_format') }}
                            </span>
                            <div class="nowrap">
                                <v-btn
                                    :color="displayFormat === 'infoView' ? 'primary' : ''"
                                    variant="text"
                                    icon="mdi-format-list-bulleted"
                                    @click="displayFormat = 'infoView'"
                                />
                                <v-btn
                                    v-if="!$conf.public.account.hideItineraryTab"
                                    :color="displayFormat === 'itineraryView' ? 'primary' : ''"
                                    variant="text"
                                    icon="mdi-map-legend"
                                    @click="displayFormat = 'itineraryView'"
                                />
                                <v-btn
                                    v-if="(isTourOperator || isAgency) && personRole !== 'dispatcher'"
                                    :color="displayFormat === 'tableView' ? 'primary' : ''"
                                    variant="text"
                                    icon="mdi-table"
                                    @click="displayFormat = 'tableView'"
                                />
                            </div>
                        </div>
                    </div>
                </v-expand-transition>
            </v-col>
            <v-col v-if="!isMobile" cols="12" md="3" class="py-0" />
        </v-row>
        <v-expand-transition>
            <v-row v-if="displayFormat === 'itineraryView' && tabSelectedName === 'info'">
                <template v-if="itineraryViewServices.length">
                    <v-col v-if="$conf.public.account.itineraryCalendarTimeline" :cols="12">
                        <itinerary-calendar-timeline
                            :services="order.services"
                            @order-add-transfer="orderAddTransfer"
                        />
                    </v-col>
                    <v-col :cols="12" :md="6">
                        <div v-if="daysBeforeTrip > 0" class="text-success mb-2">
                            {{ `${daysBeforeTrip} ${$t('day', daysBeforeTrip)} ${$t('before_trip')} :)` }}
                        </div>
                        <v-slide-group>
                            <v-btn
                                v-for="day in itineraryDays"
                                :key="day.number"
                                variant="text"
                                :href="`${$route.path}#day-${day.number}`"
                            >
                                {{ `${$t('day', 1)}  ${day.number}` }}
                            </v-btn>
                            <edit-itinerary-modal
                                v-if="isTO1"
                                :order-id="order.orderId"
                                :order-itinerary="orderItinerary"
                                @change="$emit('load-order')"
                            />
                        </v-slide-group>
                    </v-col>
                </template>
            </v-row>
        </v-expand-transition>
        <template
            v-if="
                tabSelectedName === 'info' ||
                tabSelectedName === 'communications' ||
                tabSelectedName === 'internalNotes' ||
                tabSelectedName === 'attachments'
            "
        >
            <v-row>
                <v-col v-if="tabSelectedName === 'attachments'" md="9">
                    <OrderDetailsAttachments
                        :documents="documents"
                        :order-services="order.services"
                        @remove-document="loadAttachments"
                    >
                        <service-documents-modal
                            :order-id="order.orderId"
                            :company-id="order.companyId"
                            :order-services="order.services"
                            @update-document="loadAttachments"
                        />
                    </OrderDetailsAttachments>
                </v-col>
                <v-col v-if="tabSelectedName === 'communications'" md="9">
                    <communications
                        :communications="communicationsWithoutInternalNotes"
                        :order-id="order.orderId"
                        :communications-loading="communicationsLoading"
                        :services="services.filter(({isEmptyOrder}) => !isEmptyOrder)"
                        :account-id-for-create="order.clientId"
                        :communication-data-for-create="{
                            accountId: order.clientId,
                            accountType: order.clientType === 'DIRECT_SALES' ? 'PRIVATE_CLIENT' : null,
                            contactId:
                                order.clientType === 'TA'
                                    ? order.agent.id
                                    : order.clientType === 'CORPORATE'
                                      ? order.tripOrganizer.id
                                      : null,
                        }"
                        @get-communications="loadCommunications"
                    />
                </v-col>
                <v-col v-if="tabSelectedName === 'internalNotes'" md="9">
                    <internal-notes
                        :communications="internalNotes"
                        :order-id="order.orderId"
                        :services="services.filter(({isEmptyOrder}) => !isEmptyOrder)"
                        :communications-loading="communicationsLoading"
                        @get-communications="loadCommunications"
                    />
                </v-col>
                <v-col
                    v-if="
                        tabSelectedName !== 'communications' &&
                        tabSelectedName !== 'attachments' &&
                        tabSelectedName !== 'internalNotes'
                    "
                    md="9"
                >
                    <div :class="{'order-wrapper': order.orderPackage}">
                        <order-details-traveler-list
                            v-if="$conf.public.account.orderTravelerList && tabSelectedName === 'info'"
                            :order-travelers="orderTravelers"
                            :order-end-date="orderEndDate"
                            :order="order"
                        />
                        <v-row align="center" no-gutters class="pa-0 pa-sm-2 my-6 my-sm-4" style="gap: 5px">
                            <v-col
                                v-if="isTO1 && !itineraryView && displayFormat !== 'tableView'"
                                cols="12"
                                md="5"
                                class="d-flex"
                            >
                                <v-checkbox
                                    :model-value="isALlServicesChecked"
                                    hide-details
                                    class="ml-0 ml-sm-4 mt-0 flex-shrink-1"
                                    @update:model-value="toggleAllServices"
                                >
                                    <template v-slot:label>
                                        <span class="text-subtitle-2">
                                            {{
                                                `${$t('select_all').toLocaleLowerCase()} / ${$t(
                                                    'deselect_all'
                                                ).toLocaleLowerCase()}`
                                            }}
                                        </span>
                                    </template>
                                </v-checkbox>
                            </v-col>
                            <v-spacer />
                            <v-col
                                v-if="$conf.public.account.tripPlans && (isTO1 || isTO2 || isCorporate || isAgency)"
                                cols="12"
                                md="4"
                                class="pl-1"
                            >
                                <order-details-travelers-filter
                                    :order-travelers="orderTravelers"
                                    @change="selectedTravelerName = $event"
                                />
                            </v-col>
                            <v-col
                                v-if="
                                    $conf.public.account.tripVariants && variantsAvailable && variantsAvailable.length
                                "
                                cols="12"
                                md="3"
                            >
                                <v-select
                                    v-model="variantFilter"
                                    :label="$t('show_trip_variant')"
                                    :items="variantsAvailable"
                                    density="compact"
                                    multiple
                                    :menu-props="{bottom: true, offsetY: true}"
                                    clearable
                                    hide-details
                                    variant="outlined"
                                />
                            </v-col>
                        </v-row>
                        <v-alert
                            v-if="order.orderPackage"
                            border="bottom"
                            color="info"
                            class="text-white text-uppercase mb-0"
                            v-text="order.orderPackage.packageName"
                        />
                        <div class="px-2">
                            <div v-if="order.orderPackage">
                                <component
                                    :is="packageComponent"
                                    :order="order"
                                    @cancel-service="showCancelServiceModal($event)"
                                    @cancel-tour="showCancelTourModal"
                                    @book-all-quote="bookAllQuote"
                                    @issue-ticket="$refs.issueTicketModal.show($event)"
                                />
                            </div>
                            <template v-if="itineraryView">
                                <TripAssistantClientLinkModal
                                    v-if="isTO1 && isMobile && order?.orderId && $conf.public.tripAssistant"
                                    :order-id="order.orderId"
                                    :order-travelers="orderTravelers"
                                />
                                <v-card v-if="isShowCancelledItineraryMessage" variant="outlined">
                                    <v-card-text>
                                        <span class="text-warning font-weight-medium">
                                            {{ $t('trip_cancelled_itinerary') }}
                                        </span>
                                    </v-card-text>
                                </v-card>
                                <v-card v-if="isShowNoPlansItineraryMessage" variant="outlined">
                                    <v-card-text>
                                        <span class="text-warning font-weight-medium">
                                            {{ $t('no_plans_itinerary') }}
                                        </span>
                                    </v-card-text>
                                </v-card>
                                <itinerary-info
                                    v-if="orderItinerary.description || orderItinerary.images"
                                    :info="orderItinerary"
                                />
                                <div v-for="(itineraryDay, index) in itineraryDays" :key="index">
                                    <order-destination
                                        v-if="itineraryDestination(index)"
                                        :destination="itineraryDestination(index)"
                                        :destination-location="destinationLocation(itineraryDestination(index))"
                                        @edit="$refs.destinationModal.open($event, destinationLocation($event))"
                                        @add-service="$refs.orderAddServiceModal.showWithDestination"
                                        @add-offline-service="$refs.addOfflineService.showWithDestination"
                                        @remove="removeDestination(itineraryDestination(index))"
                                    />
                                    <div>
                                        <v-card :id="`day-${itineraryDay.number}`" variant="outlined" class="mb-2">
                                            <v-alert color="grey-lighten-4" class="text-black mb-0">
                                                <div class="d-flex justify-space-between">
                                                    <div class="d-flex">
                                                        <strong
                                                            class="text-body-1 text-capitalize me-2 font-weight-medium"
                                                        >
                                                            <v-icon>mdi-weather-sunny</v-icon>
                                                            {{ $t('day', 1) }} {{ itineraryDay.number }}
                                                        </strong>
                                                        <span class="text-body-1 text-capitalize">
                                                            {{ $filters.dateFormat(itineraryDay.date) }}
                                                        </span>
                                                    </div>
                                                    <weather-service
                                                        v-if="itineraryDay.services"
                                                        :city="{
                                                            id: findAccommodationService(
                                                                itineraryDay.services.times,
                                                                'cityId'
                                                            ),
                                                            cityName: findAccommodationService(
                                                                itineraryDay.services.times,
                                                                'cityName'
                                                            ),
                                                        }"
                                                        :start-date="itineraryDay.services.date"
                                                        show-single-day
                                                    />
                                                </div>
                                            </v-alert>
                                        </v-card>
                                        <v-row class="mb-0">
                                            <v-col
                                                cols="9"
                                                v-html="itineraryDay.description && itineraryDay.description[locale]"
                                            />
                                            <v-col cols="3">
                                                <order-itinerary-day-images
                                                    :images="itineraryDayImages(itineraryDay.number)"
                                                    @show-photo-swipe="showPhotoSwipe($event)"
                                                />
                                            </v-col>
                                        </v-row>
                                        <template v-if="itineraryDay.services">
                                            <div
                                                v-for="(time, timeIndex) in itineraryDay.services.times"
                                                :key="time + timeIndex"
                                                class="d-md-flex"
                                            >
                                                <div class="mr-4 text-subtitle-1 font-weight-regular time">
                                                    <span v-if="time.type.text">
                                                        {{ time.type.text() }}
                                                    </span>
                                                    <span v-if="time.type.textFlight">
                                                        {{ time.type.textFlight() }}&nbsp;
                                                    </span>
                                                    <span v-if="time.time && time.time !== '00:00'">
                                                        {{ time.time }}
                                                    </span>
                                                </div>
                                                <div class="flex-grow-1">
                                                    <template
                                                        v-for="(service, i) in time.service"
                                                        :key="`${service.serviceId}-${i}`"
                                                    >
                                                        <component
                                                            :is="serviceComponent(service.serviceType)"
                                                            v-if="
                                                                serviceFilterBySelectedTraveler(service) &&
                                                                serviceFilterBySelectedVariant(service)
                                                            "
                                                            :service="service"
                                                            :service-tips="
                                                                tips &&
                                                                tips.filter(t => t.serviceId === service.serviceId)
                                                            "
                                                            :itinerary-service="time"
                                                            :is-package="!!service.serviceGroupId"
                                                            class="mb-5"
                                                            :order="order"
                                                            :order-id="order.orderId"
                                                            :object-id="order.objectId"
                                                            :order-end-date="orderEndDate"
                                                            :data-cy="`service-${i}`"
                                                            :variants-available="variantsAvailable"
                                                            @cancel-service="showCancelServiceModal($event)"
                                                            @issue-ticket="$refs.issueTicketModal.show($event)"
                                                            @modify-service="$emit('modify-service')"
                                                            @add-marker="addMarker"
                                                            @info-loaded="info = $event"
                                                            @show-on-map="$emit('show-on-map', $event)"
                                                        />
                                                    </template>
                                                </div>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </template>
                            <template v-if="displayFormat === 'tableView' && servicesOrderInited">
                                <v-card class="mb-6 pb-4">
                                    <v-data-table
                                        :headers="headers"
                                        :items="services"
                                        :hide-default-header="isMobile"
                                        :single-expand="isMobile"
                                        :class="{sortable: isTO1}"
                                        :row-props="rowProps"
                                        :items-per-page="-1"
                                        disable-sort
                                        class="table"
                                    >
                                        <template v-slot:bottom="{}" />
                                        <template v-slot:header.fxGainLoss="{column}">
                                            <span class="cursor-pointer">
                                                {{ column.title }}
                                                <v-btn
                                                    v-if="$conf.public.account.paymentScheduler"
                                                    size="small"
                                                    icon="mdi-information-outline"
                                                    variant="text"
                                                    location="right"
                                                    @click="isShowFxGainModal = true"
                                                />
                                            </span>
                                        </template>
                                        <template v-slot:item.mobile="{item}">
                                            <v-row class="py-4">
                                                <v-col cols="8" class="text-left">
                                                    <div class="mb-2" :class="item.statusColor">
                                                        {{ item.status }}
                                                    </div>
                                                    <div>
                                                        <v-icon size="small" start>
                                                            {{ getServiceTypeIcon(item) }}
                                                        </v-icon>
                                                        <span
                                                            class="text-subtitle-2 font-weight-medium"
                                                            v-html="item.pureTitle"
                                                        />
                                                    </div>
                                                    <div v-html="item.title" />
                                                    <div class="text--secondary" v-html="item.location" />
                                                    <div class="text-no-wrap" v-html="item.date" />
                                                    <div class="mt-2" v-html="item.travellers" />
                                                </v-col>
                                                <v-col cols="4">
                                                    <template v-if="!isAgency && commissionAndNettoCheckbox">
                                                        <span class="font-weight-bold">{{ $t('netto') }}</span>
                                                        <orders-supplier-price-label
                                                            v-if="!item.isEmptyOrder"
                                                            :item="item"
                                                        />
                                                    </template>
                                                    <span class="font-weight-bold">
                                                        {{ $t(!isAgency ? 'brutto' : 'price') }}
                                                    </span>
                                                    <template v-if="!item.isEmptyOrder && item.showAllServicesPrice">
                                                        <orders-supplier-price-label v-if="isAgency" :item="item" />
                                                        <orders-price-label v-else :item="item" />
                                                    </template>
                                                    <template v-if="showExchangeRates">
                                                        <template v-if="!$conf.public.account.paymentScheduler">
                                                            <div v-if="item.bookingXR">
                                                                <div class="font-weight-bold">
                                                                    {{ $t('bookingXR') }}
                                                                </div>
                                                                {{ item.bookingXR }}
                                                            </div>
                                                            <div v-if="item.paymentXR">
                                                                <div class="font-weight-bold">
                                                                    {{ $t('paymentXR') }}
                                                                </div>
                                                                {{ item.paymentXR }}
                                                            </div>
                                                        </template>
                                                        <div v-if="item.fxGainLoss.amount">
                                                            <div class="font-weight-bold">{{ $t('fxGainLoss') }}</div>
                                                            {{
                                                                $filters.priceFormat(
                                                                    item.fxGainLoss.amount,
                                                                    item.fxGainLoss.currency
                                                                )
                                                            }}
                                                        </div>
                                                    </template>
                                                    <v-btn
                                                        v-if="
                                                            !item.isEmptyOrder &&
                                                            voucherAvailable(item) &&
                                                            isShowVoucherBtn
                                                        "
                                                        elevation="0"
                                                        size="small"
                                                        class="cursor-pointer mt-auto mb-1"
                                                        @click="openVoucherDialog(item)"
                                                    >
                                                        {{ $t('voucher') }}
                                                    </v-btn>
                                                </v-col>
                                            </v-row>
                                        </template>
                                        <template v-slot:item.id="{item, index}">
                                            <div class="d-flex align-center">
                                                <span class="me-2">
                                                    {{ `${item.orderId}-${index + 1}` }}
                                                </span>
                                                <v-btn
                                                    size="small"
                                                    variant="text"
                                                    :icon="'mdi-magnify'"
                                                    @click="openServiceModal(item)"
                                                />
                                                <order-visible-in-reservation-btn
                                                    :process-id="item.processId"
                                                    :order-id="order.orderId"
                                                    :visible-in-reservation="item.visibleInReservation"
                                                />
                                            </div>
                                        </template>
                                        <template v-slot:item.title="{item}">
                                            <div v-if="!item.isEmptyOrder">
                                                <div class="d-flex justify-end justify-md-start">
                                                    <v-icon size="small" class="mr-1">
                                                        {{ getServiceTypeIcon(item) }}
                                                    </v-icon>
                                                    <div v-html="item.pureTitle" />
                                                </div>
                                                <div class="ps-5" v-html="item.title + item.location" />
                                            </div>
                                        </template>
                                        <template v-slot:item.date="{item}">
                                            <div class="text-no-wrap" v-html="item.date" />
                                        </template>
                                        <template v-slot:item.travellers="{item}">
                                            <span v-html="item.travellers" />
                                        </template>
                                        <template v-slot:item.status="{item}">
                                            <span class="table-status" :class="item.statusColor">
                                                {{ item.status }}
                                            </span>
                                            <div v-if="isTO1">
                                                <span>
                                                    {{ item.supplierCompanyName }}
                                                </span>
                                            </div>
                                        </template>
                                        <template v-if="!isAgency" v-slot:item.supplierPrice="{item}">
                                            <orders-supplier-price-label
                                                v-if="!item.isEmptyOrder && item.showAllServicesPrice"
                                                :show-payment-status="showNettoPaymentStatus"
                                                :item="item"
                                                :show-system-currency="showSystemNettoCurrency"
                                            />
                                        </template>
                                        <template v-slot:item.price="{item}">
                                            <template
                                                v-if="
                                                    !item.isEmptyOrder &&
                                                    item.showAllServicesPrice &&
                                                    item.showClientPrice
                                                "
                                            >
                                                <orders-supplier-price-label
                                                    v-if="isAgency"
                                                    :show-payment-status="showPaymentAndInvoiceStatus"
                                                    :item="item"
                                                />
                                                <orders-price-label
                                                    v-else
                                                    :show-payment-status="showPaymentAndInvoiceStatus"
                                                    :item="item"
                                                />
                                            </template>
                                        </template>
                                        <template v-slot:item.income="{item}">
                                            <template v-if="item.showAllServicesPrice && item.showClientPrice">
                                                <div>
                                                    {{ $filters.priceFormat(item.income.amount, item.income.currency) }}
                                                </div>
                                                <div v-if="item.income.percent" class="text-grey">
                                                    {{ `${item.income.percent}%` }}
                                                </div>
                                            </template>
                                        </template>
                                        <template v-slot:item.bookingXR="{item}">
                                            <div v-if="item.bookingXR">
                                                {{ item.bookingXR }}
                                            </div>
                                        </template>
                                        <template v-slot:item.paymentXR="{item}">
                                            <div v-if="item.paymentXR">
                                                {{ item.paymentXR }}
                                            </div>
                                        </template>
                                        <template v-slot:item.fxGainLoss="{item}">
                                            <div v-if="item.fxGainLoss.amount">
                                                {{
                                                    $filters.priceFormat(
                                                        item.fxGainLoss.amount,
                                                        item.fxGainLoss.currency
                                                    )
                                                }}
                                            </div>
                                        </template>
                                        <template v-slot:item.voucher="{item}">
                                            <v-btn
                                                v-if="!item.isEmptyOrder && voucherAvailable(item)"
                                                size="small"
                                                variant="text"
                                                class="cursor-pointer"
                                                @click="openVoucherDialog(item)"
                                            >
                                                <v-icon>mdi-file-download</v-icon>
                                            </v-btn>
                                        </template>
                                        <template v-if="!isMobile" v-slot:body.append>
                                            <br />
                                            <tr class="mb-4 text-body-2 font-weight-bold th-footer">
                                                <td class="px-4 text-start">
                                                    <br />
                                                    {{ $t('total') }}
                                                </td>
                                                <td />
                                                <td />
                                                <td />
                                                <td v-if="!isAgency && commissionAndNettoCheckbox" />
                                                <td class="px-4 text-right">
                                                    <br />
                                                    <template v-if="!isAgency && commissionAndNettoCheckbox">
                                                        <div v-for="(item, i) in nettoPrice" :key="`nettoTotal-${i}`">
                                                            <div>
                                                                <span data-cy="supplier-total">
                                                                    {{
                                                                        $filters.priceFormat(item.amount, item.currency)
                                                                    }}
                                                                </span>
                                                                <span
                                                                    v-if="
                                                                        showSystemNettoCurrency &&
                                                                        item.currency !== item.currencyClient
                                                                    "
                                                                >
                                                                    /
                                                                    {{
                                                                        $filters.priceFormat(
                                                                            item.amountInClientCurrency,
                                                                            item.currencyClient
                                                                        )
                                                                    }}
                                                                </span>
                                                            </div>
                                                            <div
                                                                v-if="item.commission && commissionAndNettoCheckbox"
                                                                class="commission font-weight-regular text-grey"
                                                            >
                                                                {{ $t('commission') }}
                                                                {{
                                                                    $filters.priceFormat(item.commission, item.currency)
                                                                }}
                                                            </div>
                                                            <div
                                                                v-if="item.commission && commissionAndNettoCheckbox"
                                                                class="commission font-weight-regular text-grey"
                                                            >
                                                                {{ $t('net_amount') }}:
                                                                {{
                                                                    $filters.priceFormat(
                                                                        item.amount - item.commission,
                                                                        item.currency
                                                                    )
                                                                }}
                                                            </div>
                                                        </div>
                                                    </template>
                                                </td>
                                                <td
                                                    v-if="!$conf.public.account.licenseeForCorporateCompany"
                                                    class="px-4 text-right"
                                                >
                                                    <br />
                                                    <div v-for="(item, i) in bruttoPrice" :key="`bruttoTotal-${i}`">
                                                        <div data-cy="client-total">
                                                            {{ $filters.priceFormat(item.amount, item.currency) }}
                                                        </div>
                                                        <div
                                                            v-if="item.commission && commissionAndNettoCheckbox"
                                                            class="commission font-weight-regular text-grey"
                                                        >
                                                            {{ $t('commission') }}
                                                            {{ $filters.priceFormat(item.commission, item.currency) }}
                                                        </div>
                                                        <div
                                                            v-if="item.commission && commissionAndNettoCheckbox"
                                                            class="commission font-weight-regular text-grey"
                                                        >
                                                            {{ $t('net_amount') }}
                                                            {{
                                                                $filters.priceFormat(
                                                                    item.amount - item.commission,
                                                                    item.currency
                                                                )
                                                            }}
                                                        </div>
                                                    </div>
                                                </td>
                                                <template v-if="showExchangeRates">
                                                    <template v-if="!$conf.public.account.paymentScheduler">
                                                        <td />
                                                        <td />
                                                    </template>
                                                    <td v-if="totalFxGainLoss.amount" class="px-4 text-right">
                                                        <br />
                                                        {{
                                                            $filters.priceFormat(
                                                                totalFxGainLoss.amount,
                                                                totalFxGainLoss.currency
                                                            )
                                                        }}
                                                    </td>
                                                    <td v-else />
                                                </template>
                                                <td
                                                    v-if="
                                                        !(
                                                            (isAgency || isTourOperator) &&
                                                            !commissionAndNettoCheckbox
                                                        ) && !$conf.public.account.licenseeForCorporateCompany
                                                    "
                                                    class="px-4 text-right"
                                                >
                                                    <change-income-dialog
                                                        v-if="isTO1 && !isEmptyOrder && quoteStatusAll"
                                                        :total-brutto-price="totalBruttoPrice"
                                                        :total-netto-price="totalNettoPriceInClientCurrency"
                                                        :total-brutto-commission="totalBruttoCommission"
                                                        :total-netto-commission="totalNettoCommissionInClientCurrency"
                                                        :total-income="totalIncomeInClientCurrency"
                                                        :netto-price="nettoPrice"
                                                        :services="
                                                            order.services.filter(s =>
                                                                checkboxServicesSelected.includes(s.serviceId)
                                                            )
                                                        "
                                                        @refresh-order="refreshOrder"
                                                    />
                                                    <div v-else-if="totalIncomeInClientCurrency" class="text-lowercase">
                                                        {{ $t('income') }}
                                                    </div>
                                                    <div v-if="totalIncomeInClientCurrency">
                                                        <div>
                                                            {{
                                                                $filters.priceFormat(
                                                                    totalIncomeInClientCurrency.amount,
                                                                    totalIncomeInClientCurrency.currency
                                                                )
                                                            }}
                                                        </div>
                                                        <div
                                                            v-if="totalIncomeInClientCurrency.percent"
                                                            class="text-grey"
                                                        >
                                                            {{ `${totalIncomeInClientCurrency.percent.toFixed(2)}%` }}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                            <br />
                                            <tr
                                                v-if="showPaymentAndInvoiceStatus"
                                                class="text-grey text-body-2 font-weight-bold th-footer"
                                            >
                                                <td class="px-4 text-start">{{ $t('paid') }}</td>
                                                <td />
                                                <td />
                                                <td />
                                                <td />
                                                <td
                                                    v-if="!isAgency && commissionAndNettoCheckbox"
                                                    class="px-4 text-right"
                                                >
                                                    <div v-for="(item, i) in nettoPrice" :key="`nettoPaid-${i}`">
                                                        <span data-cy="supplier-paid">
                                                            {{ $filters.priceFormat(item.paidAmount, item.currency) }}
                                                        </span>
                                                        <span
                                                            v-if="
                                                                showSystemNettoCurrency &&
                                                                item.currency !== item.currencyClient
                                                            "
                                                        >
                                                            /
                                                            {{
                                                                $filters.priceFormat(
                                                                    item.paidAmountInClientCurrency,
                                                                    item.currencyClient
                                                                )
                                                            }}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td
                                                    v-if="!$conf.public.account.licenseeForCorporateCompany"
                                                    class="px-4 text-right"
                                                >
                                                    <div v-for="(item, i) in bruttoPrice" :key="`bruttoPaid-${i}`">
                                                        <span data-cy="client-paid">
                                                            {{ $filters.priceFormat(item.paidAmount, item.currency) }}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr
                                                v-if="showPaymentAndInvoiceStatus && commissionAndNettoCheckbox"
                                                class="text-grey text-body-2 font-weight-bold th-footer"
                                            >
                                                <td class="px-4 text-start">{{ $t('due_to_pay') }}</td>
                                                <td />
                                                <td />
                                                <td />
                                                <td />
                                                <td
                                                    v-if="!isAgency && commissionAndNettoCheckbox"
                                                    class="px-4 text-right"
                                                >
                                                    <div v-for="(item, i) in nettoDueToPay" :key="`nettoDueToPay-${i}`">
                                                        <span data-cy="supplier-due-to-pay">
                                                            {{ $filters.priceFormat(item.dueToPay, item.currency) }}
                                                        </span>
                                                        <span
                                                            v-if="
                                                                showSystemNettoCurrency &&
                                                                item.currency !== item.currencyClient
                                                            "
                                                        >
                                                            /
                                                            {{
                                                                $filters.priceFormat(
                                                                    item.dueToPayInClientCurrency,
                                                                    item.currencyClient
                                                                )
                                                            }}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td
                                                    v-if="!$conf.public.account.licenseeForCorporateCompany"
                                                    class="px-4 text-right"
                                                >
                                                    <div
                                                        v-for="(item, i) in bruttoDueToPay"
                                                        :key="`bruttoDueToPay-${i}`"
                                                    >
                                                        <span data-cy="client-due-to-pay">
                                                            {{ $filters.priceFormat(item.dueToPay, item.currency) }}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        </template>
                                        <template v-else v-slot:body.append>
                                            <div class="mb-2 px-4 text-caption">
                                                <span class="font-weight-bold text-body-2">{{ $t('total') }}</span>
                                                <v-row>
                                                    <v-col v-if="!isAgency && commissionAndNettoCheckbox" cols="5">
                                                        <span class="font-weight-bold">{{ $t('netto') }}</span>
                                                        <div
                                                            v-for="(item, i) in nettoPrice"
                                                            :key="`nettoTotal-${i}mob`"
                                                        >
                                                            <div class="text-start">
                                                                {{ $filters.priceFormat(item.amount, item.currency) }}
                                                                <span
                                                                    v-if="
                                                                        showSystemNettoCurrency &&
                                                                        item.currency !== item.currencyClient
                                                                    "
                                                                >
                                                                    /
                                                                    {{
                                                                        $filters.priceFormat(
                                                                            item.amountInClientCurrency,
                                                                            item.currencyClient
                                                                        )
                                                                    }}
                                                                </span>
                                                            </div>
                                                            <div
                                                                v-if="item.commission && commissionAndNettoCheckbox"
                                                                class="text-grey text-start text-lowercase"
                                                            >
                                                                {{ $t('commission') }}
                                                                {{
                                                                    $filters.priceFormat(item.commission, item.currency)
                                                                }}
                                                            </div>
                                                        </div>
                                                    </v-col>
                                                    <v-col cols="4">
                                                        <span class="font-weight-bold">
                                                            {{ $t(!isAgency ? 'brutto' : 'price') }}
                                                        </span>
                                                        <div
                                                            v-for="(item, i) in bruttoPrice"
                                                            :key="`bruttoTotal-${i}mob`"
                                                        >
                                                            <div class="text-start">
                                                                {{ $filters.priceFormat(item.amount, item.currency) }}
                                                            </div>
                                                            <div
                                                                v-if="item.commission && commissionAndNettoCheckbox"
                                                                class="text-grey text-start text-lowercase"
                                                            >
                                                                {{ $t('commission') }}
                                                                {{
                                                                    $filters.priceFormat(item.commission, item.currency)
                                                                }}
                                                            </div>
                                                        </div>
                                                    </v-col>
                                                    <v-col
                                                        v-if="commissionAndNettoCheckbox"
                                                        cols="3"
                                                        class="text-right"
                                                    >
                                                        <span
                                                            v-if="totalIncomeInClientCurrency"
                                                            class="font-weight-bold"
                                                        >
                                                            {{ $t('income') }}
                                                        </span>
                                                        <div v-if="totalIncomeInClientCurrency">
                                                            <div>
                                                                {{
                                                                    $filters.priceFormat(
                                                                        totalIncomeInClientCurrency.amount,
                                                                        totalIncomeInClientCurrency.currency
                                                                    )
                                                                }}
                                                            </div>
                                                            <div
                                                                v-if="totalIncomeInClientCurrency.percent"
                                                                class="text-grey"
                                                            >
                                                                {{
                                                                    `${totalIncomeInClientCurrency.percent.toFixed(2)}%`
                                                                }}
                                                            </div>
                                                        </div>
                                                    </v-col>
                                                </v-row>
                                                <v-row v-if="showExchangeRates && totalFxGainLoss.amount > 0" dense>
                                                    <v-col cols="12" class="text-right">
                                                        <div class="font-weight-bold">{{ $t('fxGainLoss') }}</div>
                                                        {{
                                                            $filters.priceFormat(
                                                                totalFxGainLoss.amount,
                                                                totalFxGainLoss.currency
                                                            )
                                                        }}
                                                    </v-col>
                                                </v-row>
                                            </div>
                                            <div v-if="showPaymentAndInvoiceStatus" class="mb-2 px-4 text-caption">
                                                <span class="font-weight-bold text-grey text-body-2">
                                                    {{ $t('paid') }}
                                                </span>

                                                <v-row>
                                                    <v-col
                                                        v-if="!isAgency && commissionAndNettoCheckbox"
                                                        cols="5"
                                                        class="font-weight-bold"
                                                    >
                                                        <div v-for="(item, i) in nettoPrice" :key="`nettoPaid-${i}mob`">
                                                            {{ $filters.priceFormat(item.paidAmount, item.currency) }}
                                                        </div>
                                                    </v-col>
                                                    <v-col cols="4" class="font-weight-bold">
                                                        <div
                                                            v-for="(item, i) in bruttoPrice"
                                                            :key="`bruttoPaid-${i}mob`"
                                                        >
                                                            {{ $filters.priceFormat(item.paidAmount, item.currency) }}
                                                        </div>
                                                    </v-col>
                                                </v-row>
                                            </div>
                                            <div
                                                v-if="showPaymentAndInvoiceStatus && commissionAndNettoCheckbox"
                                                class="px-4 text-caption"
                                            >
                                                <span class="font-weight-bold text-grey text-body-2">
                                                    {{ $t('due_to_pay') }}
                                                </span>
                                                <v-row>
                                                    <v-col
                                                        v-if="!isAgency && commissionAndNettoCheckbox"
                                                        cols="5"
                                                        class="font-weight-bold"
                                                    >
                                                        <div
                                                            v-for="(item, i) in nettoPrice"
                                                            :key="`nettoDueToPay-${i}`"
                                                        >
                                                            {{ $filters.priceFormat(item.dueToPay, item.currency) }}
                                                        </div>
                                                    </v-col>
                                                    <v-col cols="4" class="font-weight-bold">
                                                        <div
                                                            v-for="(item, i) in bruttoDueToPay"
                                                            :key="`bruttoDueToPay-${i}`"
                                                        >
                                                            {{ $filters.priceFormat(item.dueToPay, item.currency) }}
                                                        </div>
                                                    </v-col>
                                                </v-row>
                                            </div>
                                        </template>
                                    </v-data-table>
                                </v-card>
                                <v-dialog v-model="serviceModal" content-class="selected-service-modal">
                                    <v-card>
                                        <component
                                            :is="serviceComponent(selectedService.serviceType)"
                                            v-if="selectedService.serviceType"
                                            :service="selectedService"
                                            :is-package="!!order.orderPackage"
                                            :order="order"
                                            :service-tips="
                                                tips && tips.filter(t => t.serviceId === selectedService.serviceId)
                                            "
                                            :order-id="order.orderId"
                                            :object-id="order.objectId"
                                            :order-end-date="orderEndDate"
                                            :variants-available="variantsAvailable"
                                            is-first
                                            @cancel-service="showCancelServiceModal($event)"
                                            @issue-ticket="$refs.issueTicketModal.show($event)"
                                            @add-marker="addMarker"
                                            @modify-service="$emit('modify-service')"
                                            @info-loaded="info = $event"
                                            @show-on-map="$emit('show-on-map', $event)"
                                        />
                                    </v-card>
                                </v-dialog>
                                <fx-gain-modal
                                    v-model="isShowFxGainModal"
                                    :order-id="order.orderId"
                                    :hide-total="order.fxGainLoss === undefined"
                                />
                                <voucher-dialog
                                    v-model="isShowVoucherDialog"
                                    :is-loading="voucherLoading"
                                    :title="$t('voucher')"
                                    @download-voucher="downloadVoucher(voucherService)"
                                    @send-on-email="sendVoucherOnEmail(voucherService, $event)"
                                />
                            </template>
                            <template v-else-if="servicesOrderInited">
                                <component
                                    :is="serviceComponent(service.serviceType)"
                                    v-for="(service, index) in packageServices"
                                    :key="service.serviceId"
                                    :service="service"
                                    :is-package="!!order.orderPackage"
                                    class="mb-5"
                                    :service-tips="tips && tips.filter(t => t.serviceId === service.serviceId)"
                                    :order="order"
                                    :order-id="order.orderId"
                                    :object-id="order.objectId"
                                    :is-first="index === 0"
                                    :order-end-date="orderEndDate"
                                    :data-cy="`service-${index}`"
                                    :variants-available="variantsAvailable"
                                    @cancel-service="showCancelServiceModal($event)"
                                    @issue-ticket="$refs.issueTicketModal.show($event)"
                                    @add-marker="addMarker"
                                    @modify-service="$emit('modify-service')"
                                    @info-loaded="info = $event"
                                    @show-on-map="$emit('show-on-map', $event)"
                                />
                            </template>
                        </div>
                    </div>
                    <div
                        v-if="!itineraryView && displayFormat !== 'tableView' && servicesOrderInited"
                        class="pa-0 pa-sm-2"
                    >
                        <order-destination
                            v-for="destination in destinationsSortedByDate"
                            :key="destination.id"
                            :destination="destination"
                            :destination-location="destinationLocation(destination)"
                            @edit="$refs.destinationModal.open($event, destinationLocation($event))"
                            @add-service="$refs.orderAddServiceModal.showWithDestination"
                            @add-offline-service="$refs.addOfflineService.showWithDestination"
                            @remove="removeDestination(destination)"
                        >
                            <component
                                :is="serviceComponent(service.serviceType)"
                                v-for="(service, index) in destinationServices(destination)"
                                :key="service.serviceId"
                                :service="service"
                                :service-tips="tips && tips.filter(t => t.serviceId === service.serviceId)"
                                :is-package="!!service.serviceGroupId"
                                class="mb-5"
                                :order="order"
                                :order-end-date="orderEndDate"
                                :is-first="index === 0 && !packageServices.length"
                                :data-cy="`service-${index}`"
                                :variants-available="variantsAvailable"
                                @cancel-service="showCancelServiceModal($event)"
                                @issue-ticket="$refs.issueTicketModal.show($event)"
                                @modify-service="$emit('modify-service')"
                                @add-marker="addMarker"
                                @info-loaded="info = $event"
                                @show-on-map="$emit('show-on-map', $event)"
                            >
                                <template v-slot:checkbox>
                                    <v-tooltip :text="$t('checkbox_in_order_tooltip')">
                                        <template v-slot:activator="{props}">
                                            <v-checkbox
                                                v-if="isShowCheckbox"
                                                v-bind="props"
                                                v-model="checkboxServicesSelected"
                                                :value="service.serviceId"
                                                hide-details
                                                class="mt-0 pt-0 me-2"
                                                @click.stop
                                            />
                                        </template>
                                    </v-tooltip>
                                    <order-visible-in-reservation-btn
                                        :process-id="service.processId"
                                        :order-id="order.orderId"
                                        :visible-in-reservation="service.visibleInReservation"
                                    />
                                </template>
                            </component>
                        </order-destination>
                        <v-toolbar
                            v-if="destinations.length && nonPackageServicesWithoutDestination.length"
                            color="grey-lighten-2"
                            flat
                            border
                            dense
                            class="mb-5"
                        >
                            <v-toolbar-title>{{ $t('other') }}</v-toolbar-title>
                        </v-toolbar>
                        <!--DONT PUSH OTHER ITEMS INSIDE serivceCards, SORTABLE!-->
                        <div ref="serviceCards" :class="{sortable: isTO1}" class="service-cards">
                            <component
                                :is="serviceComponent(service.serviceType)"
                                v-for="(service, index) in nonPackageServicesWithoutDestination"
                                :key="`serviceCards-${service.serviceId}`"
                                :service="service"
                                :service-tips="tips && tips.filter(t => t.serviceId === service.serviceId)"
                                :is-package="!!service.serviceGroupId"
                                class="service-card mb-5"
                                :order="order"
                                :order-end-date="orderEndDate"
                                :is-first="index === 0 && !packageServices.length"
                                :data-cy="`service-${index}`"
                                :data-processId="service.processId"
                                :variants-available="variantsAvailable"
                                @cancel-service="showCancelServiceModal($event)"
                                @issue-ticket="$refs.issueTicketModal.show($event)"
                                @modify-service="$emit('modify-service')"
                                @add-marker="addMarker"
                                @info-loaded="info = $event"
                                @show-on-map="$emit('show-on-map', $event)"
                            >
                                <template v-slot:checkbox>
                                    <div class="d-flex flex-column flex-shrink-0 align-center">
                                        <v-tooltip :text="$t('checkbox_in_order_tooltip')">
                                            <template v-slot:activator="{props}">
                                                <v-checkbox
                                                    v-if="isShowCheckbox"
                                                    v-bind="props"
                                                    v-model="checkboxServicesSelected"
                                                    :value="service.serviceId"
                                                    hide-details
                                                    @click.stop
                                                />
                                            </template>
                                        </v-tooltip>
                                    </div>
                                </template>
                                <template v-slot:icons>
                                    <order-visible-in-reservation-btn
                                        :process-id="service.processId"
                                        :order-id="order.orderId"
                                        :visible-in-reservation="service.visibleInReservation"
                                    />
                                </template>
                            </component>
                            <v-btn
                                v-if="
                                    !$conf.public.account.hideAddServiceBtn &&
                                    personRole !== 'dispatcher' &&
                                    showAddServiceBtn
                                "
                                color="primary"
                                class="add-service add-service--big text-white"
                                block
                                data-cy="add-service-button"
                                :size="$breakpoint.smAndDown ? 'small' : 'default'"
                                @click="orderAddService"
                                v-text="$t('add_service')"
                            />
                        </div>
                    </div>

                    <div v-if="displayFormat !== 'tableView'">
                        <v-btn
                            v-if="servicesCanBeCancelled.length > 0 && sortedServices.length > 1 && !order.orderPackage"
                            variant="flat"
                            class="mb-5"
                            @click="showCancelServicesAllModal"
                        >
                            {{ $t('cancel') + ' ' + $t('all') }}
                        </v-btn>
                        <v-btn
                            v-if="showBookAllBtn"
                            variant="flat"
                            color="primary"
                            class="mb-5"
                            @click="bookAllQuote()"
                        >
                            {{
                                `${$t('book')} ${
                                    checkboxServicesSelected.length === nonPackageServices.length
                                        ? $t('all')
                                        : $t('selected')
                                }`
                            }}
                        </v-btn>
                        <v-btn
                            v-if="$conf.public.account.orderDestinations"
                            color="primary"
                            variant="text"
                            class="mb-5"
                            @click="$refs.destinationModal.open()"
                        >
                            {{ $t('add_destination') }}
                        </v-btn>
                    </div>
                </v-col>
                <v-col v-if="!$breakpoint.smAndDown" class="order-details__sidebar" md="3">
                    <div class="order-summary">
                        <TripAssistantClientLinkModal
                            v-if="isTO1 && itineraryView && order?.orderId && $conf.public.tripAssistant"
                            :order-id="order.orderId"
                            :order-travelers="orderTravelers"
                        />
                        <order-details-summary
                            class="mb-4"
                            :checkbox-services-selected="checkboxServicesSelected"
                            :order-travelers="orderTravelers"
                            :vat-calculated-amount-by-each-service-brutto="vatCalculatedAmountByEachServiceBrutto"
                            :vat-calculated-amount-by-each-service-netto="vatCalculatedAmountByEachServiceNetto"
                            :order-prices-calculated-brutto="bruttoPrice"
                            :order-prices-calculated-netto="nettoPrice"
                            :total-income-in-client-currency="
                                !isEmptyOrder && totalIncomeInClientCurrency ? totalIncomeInClientCurrency : {}
                            "
                            @send-request="$emit('modify-service')"
                            @order-add-service="orderAddService"
                            @init-service-order="initServiceOrder"
                        />
                        <weather-service
                            v-if="order.services[0]"
                            :city="{
                                id: order.services[0].serviceDetails[0].cityId,
                                cityName: order.services[0].serviceDetails[0].cityName,
                            }"
                            :start-date="weatherStartDate"
                            :end-date="weatherEndDate"
                            class="mb-4"
                        />
                        <client-only>
                            <v-card v-if="isShowMap && !isMobile" class="order-details__map mb-4" variant="outlined">
                                <v-card-text>
                                    <order-details-map
                                        :marker-coordinates="orderDetailsMapMarkers"
                                        :destinations="destinationsSortedByDate"
                                        :destination-locations="destinationLocations"
                                    />
                                </v-card-text>
                            </v-card>
                        </client-only>
                        <order-details-tips
                            v-if="!isMobile && $conf.public.account.showTips"
                            :tips="tips && tips.filter(t => !t.serviceId)"
                            class="mb-2"
                            :order-id="order.orderId"
                            :loaded="tipsLoaded"
                        />
                    </div>
                </v-col>
            </v-row>
        </template>
        <v-row v-if="tabSelectedName === 'summary'">
            <order-details-summary
                :checkbox-services-selected="checkboxServicesSelected"
                outlined
                :vat-calculated-amount-by-each-service-brutto="vatCalculatedAmountByEachServiceBrutto"
                :vat-calculated-amount-by-each-service-netto="vatCalculatedAmountByEachServiceNetto"
                :order-prices-calculated-brutto="bruttoPrice"
                :order-prices-calculated-netto="nettoPrice"
                :order-travelers="orderTravelers"
                :total-income-in-client-currency="
                    !isEmptyOrder && totalIncomeInClientCurrency ? totalIncomeInClientCurrency : {}
                "
                @send-request="$emit('modify-service')"
                @order-add-service="orderAddService"
            />
        </v-row>
        <v-row v-if="tabSelectedName === 'weather'">
            <v-col>
                <weather-service
                    :city="{
                        id: order.services[0].serviceDetails[0].cityId,
                        cityName: order.services[0].serviceDetails[0].cityName,
                    }"
                    :start-date="weatherStartDate"
                    :end-date="weatherEndDate"
                    class="mb-4"
                />
            </v-col>
        </v-row>
        <v-row v-if="tabSelectedName === 'map'">
            <v-col>
                <client-only>
                    <v-card variant="outlined">
                        <v-card-text>
                            <order-details-map
                                :marker-coordinates="orderDetailsMapMarkers"
                                :destinations="destinationsSortedByDate"
                                :destination-locations="destinationLocations"
                            />
                        </v-card-text>
                    </v-card>
                </client-only>
            </v-col>
        </v-row>
        <v-row v-if="tabSelectedName === 'tips' && $conf.public.account.showTips">
            <v-col>
                <order-details-tips
                    class="mb-2"
                    :tips="tips && tips.filter(t => !t.serviceId)"
                    :order-id="order.orderId"
                    :loaded="tipsLoaded"
                />
            </v-col>
        </v-row>
        <conditions-modal disable-currency-conversion />
        <cancel-service-modal ref="cancelServiceModal" @cancel-service="$emit('cancel-service')" />
        <cancel-services-all-modal ref="cancelServicesAllModal" @cancel-service="$emit('cancel-service')" />
        <cancel-tour-modal ref="cancelTourModal" @cancel-service="$emit('cancel-service')" />
        <cancel-service-manual-modal ref="cancelServiceManualModal" />
        <invoices-modal />
        <invoices-issue-modal @issued-invoice="$emit('issued-invoice', $event)" />
        <modify-modal @modify-service="$emit('modify-service')" />
        <modify-manual-modal
            :info="info"
            :client-type="order.clientType"
            :order-id="order.orderId"
            :customer-company-id="order.customerCompanyId"
            @send-request="refreshOrder"
        />
        <invoices-pay-modal @issued-invoice="$emit('issued-invoice', $event)" />
        <issue-ticket-modal ref="issueTicketModal" @issued-ticket="$emit('issued-ticket')" />
        <reservations-status-modal />
        <ticket-voiding-modal />
        <v-photo-swipe
            v-if="photoSwipeItems?.length"
            ref="photoSwipe"
            :items="photoSwipeItems"
            :photo-swipe-options="photoSwipeOptions"
        />
        <service-notes-modal
            :communications="internalNotes"
            :communications-loading="communicationsLoading"
            :order-id="order.orderId"
            @get-communications="loadCommunications"
        />
        <due-to-confirm-date-service-modal :order-id="order.orderId" @load-order="loadOrder" />
        <service-comments-modal />
        <service-feedback-modal />
        <order-add-service-modal
            ref="orderAddServiceModal"
            v-model="isShowOrderAddServiceModal"
            :order="order"
            :prefill-transfer-data="isOrderAddServicePrefillTransferData"
            :destination-locations="destinationLocations"
        />
        <div
            v-if="
                !$conf.public.account.hideAddServiceBtn &&
                isMobile &&
                tabSelectedName === 'info' &&
                personRole !== 'dispatcher'
            "
            class="d-flex flex-column full-width"
            style="position: fixed; bottom: 0; left: 0; background: white"
        >
            <v-btn
                color="primary"
                class="text-white rounded-0 mb-2"
                block
                data-cy="add-service-button"
                :size="$breakpoint.smAndDown ? 'small' : 'default'"
                @click="orderAddService"
                v-text="$t('add_service')"
            />
            <add-offline-service-dialog
                :order-travelers="orderTravelers"
                :order-process-id="order.processId"
                :client-person="order.clientPerson"
                :client-type="order.clientType"
                :order="order"
                :customer-company-id="order.customerCompanyId"
                @send-request="refreshOrder"
            />
        </div>
        <add-offline-service-dialog
            ref="addOfflineService"
            :order-travelers="orderTravelers"
            :order-process-id="order.processId"
            :client-type="order.clientType"
            :customer-company-id="order.customerCompanyId"
            :order="order"
            @send-request="refreshOrder"
        >
            <template v-slot:activator>&nbsp;</template>
        </add-offline-service-dialog>
        <order-destination-modal
            ref="destinationModal"
            :order="order"
            :destinations="destinations"
            :destination-locations="destinationLocations"
        />
        <hotel-room-info-modal />
        <hotel-meal-type-info-modal />
    </div>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Watch, Provide} from 'vue-facing-decorator'
    import FlightService from '~/components/account/orderServices/FlightService.vue'
    import AccommodationService from '~src/components/account/orderServices/accommodationService.src'
    import TransferService from '~src/components/account/orderServices/transferService.src'
    import CarService from '@/components/account/orderServices/CarService.vue'
    import InsuranceService from '@/components/account/orderServices/InsuranceService.vue'
    import TourService from '@/components/account/orderServices/TourService.vue'
    import ActivityService from '~/components/account/orderServices/ActivityService.vue'
    import CruiseService from '~/components/account/orderServices/CruiseService.vue'
    import VisaService from '~/components/account/orderServices/VisaService.vue'
    import ConditionsModal from '@/components/modals/ConditionsModal.vue'
    import CancelServiceModal from '@/components/account/modals/CancelServiceModal.vue'
    import CancelServiceManualModal from '@/components/account/modals/CancelServiceManualModal.vue'
    import InvoicesModal from '~src/components/modals/invoicesModal.src'
    import OrderPrice from '@/components/account/snippets/OrderPrice.vue'
    import {capitalizeFirstLetter, clone, getServiceTypeIcon} from '~src/utils/helpers'
    import getOrderTravelers from '@/adapters/orderTravelersAdapter'
    import ModifyModal from '@/components/account/modify/ModifyModal.vue'
    import InvoicesPayModal from '@/components/modals/InvoicesPayModal.vue'
    import IssueTicketModal from '@/components/modals/IssueTicketModal.vue'
    import ChatModal from '@/components/modals/ChatModal.vue'
    import TotalOrderPrices from '~src/components/account/snippets/totalOrderPrices.src'
    import ItineraryInfo from '@/components/info/ItineraryInfo.vue'
    import VPhotoSwipe from '@/components/snippets/VPhotoSwipe.vue'
    import OrderItineraryDayImages from '@/components/account/snippets/OrderItineraryDayImages.vue'
    import PaymentStatusLabel from '~src/components/account/snippets/paymentStatusLabel.src'
    import OrderAddServiceModal from '~src/components/account/modals/orderAddServiceModal.src'
    import PriceLabel from '~src/components/snippets/priceLabel.src'
    import CancelServicesAllModal from '@/components/account/modals/CancelServicesAllModal.vue'
    import CancelTourModal from '@/components/account/modals/CancelTourModal.vue'
    import ReservationsStatusModal from '~src/components/account/modals/ReservationsStatusModal.vue'
    import TicketVoidingModal from '@/components/account/modals/TicketVoidingModal.vue'
    import ServiceDocumentsModal from '@/components/account/modals/ServiceDocumentsModal.vue'
    import OrderDetailsMap from '@/components/account/OrderDetailsMap.vue'
    import OrderDetailsTips from '@/components/account/OrderDetailsTips.vue'
    import WeatherService from '@/components/WeatherService.vue'
    import Communications from '@/components/account/communications/Communications.vue'
    import AddOfflineServiceDialog from '~src/components/account/offlineServices/addOfflineServiceDialog.src'
    import {VoucherMixin} from '@/components/account/mixins/VoucherMixin.vue'
    import OrderDetailsSummary from '~src/components/account/orderDetailsSummary.src'
    import OrderHistory from '@/components/account/OrderHistory.vue'
    import OrdersPriceLabel from '~src/components/account/snippets/ordersPriceLabel.src'
    import OrdersSupplierPriceLabel from '~src/components/account/snippets/ordersSupplierPriceLabel.src'
    import ChangeTotalPriceDialog from '@/components/booking/ChangeTotalPriceDialog.vue'
    import ChangeIncomeDialog from '@/components/account/ChangeIncomeDialog.vue'
    import InternalNotes from '@/components/account/internalNotes/InternalNotes.vue'
    import EditItineraryModal from '@/components/account/modals/EditItineraryModal.vue'
    import {EventBus, ORDER_SERVICE_BOOK, UPDATE_ATTACHMENTS} from '@/utils/event-bus'
    import {convertPrice} from '@/utils/filters'
    import OrderDetailsTravelerList from '~src/components/account/orderDetailsTravelerList.src'
    import OrderDetailsTravelersFilter from '@/components/account/OrderDetailsTravelersFilter.vue'
    import ServiceNotesModal from '@/components/account/modals/ServiceNotesModal.vue'
    import DueToConfirmDateServiceModal from '@/components/account/modals/DueToConfirmDateServiceModal.vue'
    import FxGainModal from '@/components/account/modals/FxGainModal.vue'
    import Sortable from 'sortablejs'
    import ServiceCommentsModal from '@/components/account/modals/ServiceCommentsModal.vue'
    import ServiceFeedbackModal from '@/components/account/modals/ServiceFeedbackModal.vue'
    import {searchRequest as searchRequestTransfers} from '@/utils/transfers/transfers-blank-states'
    import OrderDestinationSelect from '@/components/account/snippets/OrderDestinationSelect.vue'
    import OfflineService from '~/components/account/orderServices/OfflineService.vue'
    import ExtraserviceService from '~/components/account/orderServices/ExtraserviceService.vue'
    import PackageInfo from '~/components/account/orderServices/PackageInfo.vue'
    import PackageTourInfo from '~/components/account/orderServices/PackageTourInfo.vue'
    import {OrderDestinationsMixin} from '@/components/account/mixins/OrderDestinationsMixin.vue'
    import OrderDestination from '@/components/account/OrderDestination.vue'
    import OrderDestinationModal from '@/components/account/modals/OrderDestinationModal.vue'
    import OrderVisibleInReservationBtn from '~/components/account/snippets/OrderVisibleInReservationBtn.vue'
    import CampaignSelect from '~/components/account/orders/CampaignSelect.vue'
    import ClassificationSelect from '~/components/account/orders/ClassificationSelect.vue'
    import OrderDetailsReservationStatusEditor from '~/components/account/orders/OrderDetailsReservationStatusEditor.vue'
    import OrderDetailsTitleEditor from '~/components/account/orders/OrderDetailsTitleEditor.vue'
    import OrderDetailsManageReservation from '~/components/account/orders/OrderDetailsManageReservation.vue'
    import OrderDetailsSettingsModal from '~/components/account/orders/OrderDetailsSettingsModal.vue'
    import OrderDetailsAttachments from '~/components/account/orders/OrderDetailsAttachments.vue'
    import TripAssistantClientLinkModal from '~/components/account/modals/tripAssistant/TripAssistantClientLinkModal.vue'
    import HotelRoomInfoModal from '~/components/modals/HotelRoomInfoModal.vue'
    import HotelMealTypeInfoModal from '~/components/hotels/modals/HotelMealTypeInfoModal.vue'
    import InvoicesIssueModal from '~/components/modals/InvoicesIssueModal.vue'
    // TODO Need total clean code
    @Component({
        components: {
            InvoicesIssueModal,
            HotelMealTypeInfoModal,
            HotelRoomInfoModal,
            TripAssistantClientLinkModal,
            OrderDetailsAttachments,
            OrderDetailsSettingsModal,
            OrderDetailsManageReservation,
            OrderDetailsTitleEditor,
            OrderDetailsReservationStatusEditor,
            ClassificationSelect,
            CampaignSelect,
            OrderDestinationSelect,
            ServiceFeedbackModal,
            ItineraryCalendarTimeline: defineAsyncComponent(
                () => import('@/components/account/ItineraryCalendarTimeline.vue')
            ),
            FxGainModal,
            DueToConfirmDateServiceModal,
            ServiceNotesModal,
            OrderDetailsTravelerList,
            InternalNotes,
            ChangeIncomeDialog,
            ChangeTotalPriceDialog,
            EditItineraryModal,
            AddOfflineServiceDialog,
            Communications,
            OrderDetailsTips,
            OrderDetailsMap,
            ModifyManualModal: defineAsyncComponent(
                () => import('~src/components/account/modify/manual/ModifyManualModal.vue')
            ),
            CancelTourModal,
            CancelServicesAllModal,
            PriceLabel,
            OrderAddServiceModal,
            OrderItineraryDayImages,
            ItineraryInfo,
            TotalOrderPrices,
            InvoicesPayModal,
            CancelServiceModal,
            CancelServiceManualModal,
            ConditionsModal,
            FlightService,
            AccommodationService,
            TransferService,
            CarService,
            InsuranceService,
            TourService,
            ActivityService,
            VisaService,
            PackageInfo,
            PackageTourInfo,
            ExtraserviceService,
            OfflineService,
            InvoicesModal,
            OrderPrice,
            ModifyModal,
            IssueTicketModal,
            ChatModal,
            VPhotoSwipe,
            PaymentStatusLabel,
            ServiceDocumentsModal,
            ReservationsStatusModal,
            TicketVoidingModal,
            WeatherService,
            OrderDetailsSummary,
            OrdersPriceLabel,
            OrdersSupplierPriceLabel,
            CruiseService,
            ServiceCommentsModal,
            OrderHistory,
            OrderDetailsTravelersFilter,
            OrderDestinationModal,
            OrderDestination,
            OrderVisibleInReservationBtn,
        },
    })
    export class OrderDetails extends mixins(VoucherMixin, OrderDestinationsMixin) {
        snackbar = {
            show: false,
            message: '',
        }
        orderItinerary = {}
        photoSwipeOptions = {index: 0}
        photoSwipeItems = []
        itineraryLink = ''
        orderDetailsMapMarkers = []
        selectedTabIndex = 0
        tips = null
        tipsLoaded = false
        communications = []
        communicationsLoading = false
        serviceModal = false
        selectedService = {}
        weatherStartDate = null
        weatherEndDate = null
        checkboxServicesSelected = []
        isShowFxGainModal = false
        servicesOrder = []
        info = null
        showSettingsModal = false
        isShowOrderAddServiceModal = false
        isOrderAddServicePrefillTransferData = true
        displayFormat = 'infoView'
        servicesOrderInited = false
        selectedTravelerName = null
        documents = []

        variantFilter = []
        @Provide packageServicesLength
        @Emit()
        loadOrder() {}

        @Watch('nonPackageServices.length')
        onNonPackageServicesLengthChange() {
            this.checkboxServicesSelected = [...this.nonPackageServices, ...this.packageServices].map(s => s.serviceId)
        }

        @Watch('variantFilter')
        onVariantFilterChange() {
            this.checkboxServicesSelected = [...this.nonPackageServices, ...this.packageServices]
                .filter(s => this.serviceFilterBySelectedVariant(s))
                .map(s => s.serviceId)
        }

        @Watch('displayFormat')
        async switchDisplayFormat() {
            if (this.displayFormat === 'itineraryView') {
                persistentStore.SET_ITINERARY_VIEW(true)
                await this.loadOrderItinerary()
            } else {
                persistentStore.SET_ITINERARY_VIEW(false)
                await this.initSortable()
            }
        }

        @Watch('showCancelledServices')
        async changeShowCancelledServices() {
            this.initServiceOrder()
            this.toggleAllServices(!this.isALlServicesChecked)
        }

        @Watch('tabSelectedName')
        onTabSelectedNameChange() {
            this.initSortable()
        }

        async mounted() {
            this.checkboxServicesSelected = [...this.nonPackageServices, ...this.packageServices].map(s => s.serviceId)
            if (this.packageServices?.length) {
                this.packageServicesLength = this.packageServices.filter(
                    ({serviceType}) => serviceType !== 'CUSTOMCOMPLEXTOUR'
                ).length
            }
            await this.initServiceOrder()
            await this.$store.restored
            this.selectedTabIndex = !this.order.services?.length ? 0 : persistentStore.selectedTabIndex
            if (this.tabSelectedName === 'info') {
                if (
                    (authStore.isCorporate && authStore.user.isEmployeeOrExternalCorp && this.gotMoreTwoServices) ||
                    this.itineraryView
                ) {
                    this.displayFormat = 'itineraryView'
                } else {
                    await this.switchDisplayFormat()
                }
            }
            if (this.$conf.public.account.showTips) {
                this.tips = await this.loadTips()
            }
            await this.loadCommunications()
            if (this.order?.orderId) await this.loadAttachments()
            if (this.order.services?.length) this.prepareWeatherDate()

            EventBus.$on(UPDATE_ATTACHMENTS, this.loadAttachments)
        }

        beforeUnmount() {
            persistentStore.SET_SELECTED_TAB_INDEX(this.selectedTabIndex)
            EventBus.$off(UPDATE_ATTACHMENTS, this.loadAttachments)
        }

        get isStaticPackage() {
            return !!this.customComplexTourInfo
        }

        get customComplexTourInfo() {
            return this.order.services?.find(({serviceType}) => serviceType === 'CUSTOMCOMPLEXTOUR')
        }

        async loadAttachments() {
            try {
                this.documents = await this.$api.documents.get({orderId: this.order.orderId})
            } catch (e) {
                this.documents = []
            }
        }

        async refreshOrder() {
            await runtimeStore.refreshOrder()
        }

        rowProps(data) {
            return {
                'data-processId': `${data.item.processId}`,
            }
        }

        async initSortable() {
            this.servicesOrder.forEach((item, index) => {
                item.orderNumber = index + 1
            })
            await this.$nextTick()
            if (this.tabSelectedName !== 'info') return
            const el = this.displayFormat === 'tableView' ? document.querySelector('tbody') : this.$refs.serviceCards
            if (!this.isTO1 || !this.order.services?.length) return

            const sort = async options => {
                const sortedProcessIds = [...options.to.children]
                    .map(el => el.getAttribute('data-processId'))
                    .filter(el => !!el)
                const oldServicesOrderProcessIds = this.servicesOrder.map(el => el.processId)
                const newServicesOrderProcessIds = []

                let usedIndex = 0
                oldServicesOrderProcessIds.forEach(oldEl => {
                    if (sortedProcessIds.find(sortedEl => sortedEl === oldEl)) {
                        newServicesOrderProcessIds.push(sortedProcessIds[usedIndex])
                        usedIndex++
                    } else {
                        newServicesOrderProcessIds.push(oldEl)
                    }
                })

                const newServicesOrder = newServicesOrderProcessIds.map((el, index) => ({
                    processId: el,
                    orderNumber: index + 1,
                }))

                try {
                    await this.$api.orders.put(this.order.orderId, {
                        servicesOrder: newServicesOrder,
                    })
                    this.servicesOrder = newServicesOrder

                    //workaround for tableview
                    if (this.displayFormat === 'tableView') {
                        this.servicesOrderInited = false

                        await this.$nextTick()

                        this.servicesOrderInited = true
                    }

                    // eslint-disable-next-line no-empty
                } catch (e) {}
            }
            Sortable.create(el, {
                onEnd(options) {
                    sort(options)
                },
            })
        }

        async orderAddService() {
            this.isOrderAddServicePrefillTransferData = true
            await this.$nextTick()
            this.isShowOrderAddServiceModal = true
        }

        async orderAddTransfer({
            departureHotelName,
            departureCityId,
            departurePlace,
            arrivalHotelName,
            arrivalCityId,
            arrivalPlace,
            dateTime,
        }) {
            this.isOrderAddServicePrefillTransferData = false
            this.isShowOrderAddServiceModal = true

            await this.$nextTick()

            this.$refs.orderAddServiceModal.initialized = false

            transfersRuntimeStore.SET_DEPARTURE_POINT({})
            transfersRuntimeStore.SET_ARRIVAL_POINT({})

            await transfersRuntimeStore.loadDeparturePoint({
                ...(departureHotelName && {departureHotelName}),
                ...(departureCityId && {departureCityId}),
                ...(departurePlace && {departurePlace}),
            })
            await transfersRuntimeStore.loadArrivalPoint({
                ...(arrivalHotelName && {arrivalHotelName}),
                ...(arrivalCityId && {arrivalCityId}),
                ...(arrivalPlace && {arrivalPlace}),
            })

            transfersStore.SET_SEARCH_REQUEST({
                ...searchRequestTransfers(),
                ...(departureCityId && {departureCityId}),
                ...(arrivalCityId && {arrivalCityId}),
                ...(dateTime && {dateTime: dateTime}),
            })

            this.$refs.orderAddServiceModal.initialized = true

            await this.$nextTick()

            const transferTabIndex = this.$refs.orderAddServiceModal?.$refs.searchFormTabs?.activeProducts.findIndex(
                p => p === 'transfers'
            )
            if (transferTabIndex && transferTabIndex !== -1) {
                this.$refs.orderAddServiceModal.$refs.searchFormTabs.productTabs = transferTabIndex
            }
        }

        initServiceOrder() {
            this.servicesOrderInited = false
            const servicesOrder = this.getSortedServices(this.order.services).map(
                ({orderNumber, processId}, index) => ({
                    processId,
                    orderNumber: orderNumber || index + 1,
                })
            )
            servicesOrder.sort((a, b) => a.orderNumber - b.orderNumber)
            this.servicesOrder = servicesOrder
            this.servicesOrderInited = true
        }

        async loadTips() {
            try {
                this.tipsLoaded = false
                return (await this.$api.tips.get({orderId: this.order.orderId, orderType: this.order.orderType})).tips
                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.tipsLoaded = true
            }
        }

        async loadOrderItinerary() {
            const orderId = this.order.orderId
            try {
                await Promise.all([
                    (async () => {
                        this.orderItinerary = await this.$api.orderItinerary.get({orderId})
                    })(),
                    /*(async () => {
                        const key = await this.$api.itineraryKey.get(orderId)
                        this.itineraryLink =
                            window.location.origin +
                            this.$router.resolve({
                                name: 'order-details',
                                params: {id: orderId},
                                query: {key},
                            }).href
                    })(),*/
                ])
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        async loadCommunications() {
            try {
                this.communicationsLoading = true

                this.communications = (
                    await this.$api.communications.get({
                        orderId: this.order.orderId,
                    })
                ).communications

                this.communicationsLoading = false
                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.communicationsLoading = false
            }
        }

        addMarker(marker) {
            if (!this.orderDetailsMapMarkers.find(e => e.lat === marker.lat && e.lng === marker.lng)) {
                this.orderDetailsMapMarkers.push(marker)
            }
        }

        serviceComponent(serviceType) {
            if (serviceType === 'OWNCHARTER') {
                serviceType = 'FLIGHT'
            } else if (serviceType === 'OWNEXTRASERVICE') {
                serviceType = 'EXTRASERVICE'
            } else if (serviceType === 'CARRENT') {
                serviceType = 'CAR'
            } else if (serviceType === 'OWNVISA') {
                serviceType = 'VISA'
            } else if (serviceType === 'EXTRASERVICE') {
                serviceType = 'OFFLINE'
            }
            return resolveComponent(`${capitalizeFirstLetter(serviceType.toLowerCase())}Service`)
        }

        getServiceTypeIcon(service) {
            return getServiceTypeIcon(service)
        }

        buildItineraryForAccommodation(services) {
            const createNewProp = (dateTime, sourceDateTime, service, firstPart) => {
                dateTime[sourceDateTime[0]] = {
                    date: sourceDateTime[0],
                    times: [
                        {
                            type: {
                                text:
                                    service.serviceType === 'ACCOMMODATION'
                                        ? () => (firstPart ? this.$t('check_in') : this.$t('check_out'))
                                        : null,
                                textFlight:
                                    service.serviceType === 'FLIGHT'
                                        ? () => (firstPart ? this.$t('flights.departure') : this.$t('flights.arrival'))
                                        : null,
                                start: !!firstPart && service.firstFlightSegment !== false,
                            },
                            time: sourceDateTime[1],
                            service: [service],
                        },
                    ],
                }
            }
            const addToPropNewTime = (dateTime, sourceDateTime, service, firstPart) => {
                dateTime[sourceDateTime[0]].times.push({
                    type: {
                        text:
                            service.serviceType === 'ACCOMMODATION'
                                ? () => (firstPart ? this.$t('check_in') : this.$t('check_out'))
                                : null,
                        textFlight:
                            service.serviceType === 'FLIGHT'
                                ? () => (firstPart ? this.$t('flights.departure') : this.$t('flights.arrival'))
                                : null,
                        start: !!firstPart,
                    },
                    time: sourceDateTime[1],
                    service: [service],
                })
            }
            const addToExistTime = (dateTime, sourceDateTime, service, firstPart, index) => {
                dateTime[sourceDateTime[0]].times[index].service.push(service)
            }
            const getNormalizedStartTime = (start, service) => {
                if (service.serviceType === 'TRANSFER') {
                    start[1] = service.serviceDetailsContainer.transfer.pickupTime
                        ? service.serviceDetailsContainer.transfer.pickupTime
                        : start[1]
                }

                if (service.serviceType === 'ACTIVITY') {
                    start[1] = service.serviceDetails[0].pickupTime ? service.serviceDetails[0].pickupTime : start[1]
                }
            }
            const dateTime = {}
            services.forEach(service => {
                const start = service.startDateTime.split(' ')
                getNormalizedStartTime(start, service)
                const end = service.endDateTime.split(' ')
                if (!dateTime[start[0]]) {
                    createNewProp(dateTime, start, service, true)
                } else {
                    const gotSameTimeStart = dateTime[start[0]].times.findIndex(time => time.time === start[1])
                    if (gotSameTimeStart > -1) {
                        addToExistTime(dateTime, start, service, true, gotSameTimeStart)
                    } else {
                        addToPropNewTime(dateTime, start, service, true)
                    }
                }
                if (end[1] !== '00:00') {
                    const serviceEndCopy = clone(service)
                    serviceEndCopy.end = true
                    if (!dateTime[end[0]]) {
                        createNewProp(dateTime, end, serviceEndCopy, false)
                    } else {
                        const gotSameTimeEnd = dateTime[end[0]].times.findIndex(time => time.time === end[1])
                        if (gotSameTimeEnd > -1) {
                            addToExistTime(dateTime, end, serviceEndCopy, false, gotSameTimeEnd)
                        } else {
                            addToPropNewTime(dateTime, end, serviceEndCopy, false)
                        }
                    }
                }
            })
            return dateTime
        }

        itineraryDayImages(dayNumber) {
            return this.orderItinerary?.images?.filter(image => image.dayNumber === dayNumber) || []
        }

        showPhotoSwipe({images, index}) {
            this.photoSwipeOptions.index = index
            this.photoSwipeItems = images?.map(({url, title}) => ({
                src: this.$filters.imageUrl(url, 800),
                w: 0,
                h: 0,
                title: title === 'null' ? null : title,
            }))
            this.$refs.photoSwipe.openLightbox(index)
        }

        showCancelServiceModal(service) {
            if (this.$conf.public.account.cancelServiceManualModal || service.status === 'Confirmation pending') {
                this.$refs.cancelServiceManualModal.show(service)
            } else {
                this.$refs.cancelServiceModal.show(service, this.order)
            }
        }

        showCancelServicesAllModal() {
            this.$refs.cancelServicesAllModal?.show(this.sortedServices, this.order)
        }

        bookAllQuote(packageServices) {
            const services =
                packageServices ||
                this.servicesQuote.filter(({serviceId}) => this.checkboxServicesSelected.includes(serviceId))

            EventBus.$emit(ORDER_SERVICE_BOOK, {services, order: this.order})
        }

        showCancelTourModal() {
            this.$refs.cancelTourModal?.show(
                this.sortedServices.filter(s => !!s.serviceContainerId),
                this.order
            )
        }

        getTabDisabled(tab) {
            return (
                (tab.name === 'tips' && !this.tips?.length) ||
                (tab.name === 'map' && !this.orderDetailsMapMarkers?.length)
            )
        }

        voucherAvailable(service) {
            return runtimeStore.isServiceVoucherAvailable(service)
        }

        openVoucherDialog(service) {
            this.isShowVoucherDialog = true
            this.voucherService = service
        }

        async openServiceModal(item) {
            this.selectedService = await this.order.services.find(service => service.serviceId === item.serviceId)
            this.serviceModal = true
        }

        prepareWeatherDate() {
            this.order.services.forEach((service, index) => {
                if (index === 0) {
                    this.weatherStartDate = service.startDateTime
                    this.weatherEndDate = service.endDateTime
                } else {
                    if (
                        this.$dateFns.isAfter(
                            this.$dateFns.parseISO(this.weatherStartDate),
                            this.$dateFns.parseISO(service.startDateTime)
                        )
                    ) {
                        this.weatherStartDate = service.startDateTime
                    }
                    if (
                        this.$dateFns.isBefore(
                            this.$dateFns.parseISO(this.weatherEndDate),
                            this.$dateFns.parseISO(service.endDateTime)
                        )
                    ) {
                        this.weatherEndDate = service.endDateTime
                    }
                }
            })
        }

        steppedSubtraction(...args) {
            let i = 0
            let sum = 0

            while (i < args.length) {
                if (i === 0) sum = args[i]
                else sum = +(sum - args[i]).toFixed(2)

                i++
            }

            return sum
        }

        // TODO: temporary solution, backend edits required
        findAccommodationService(times, key) {
            const index = times.findIndex(el => el.service[0].serviceType === 'ACCOMMODATION')
            if (index >= 0) return times[index]?.service[0].serviceDetails[0][key]

            return times[0]?.service[0].serviceDetails[0][key]
        }

        calculateTotalPriceFromArray(prices) {
            let totalPrice

            prices.forEach(price => {
                if (!totalPrice) {
                    totalPrice = {...price, amountCent: price.amount * 100}
                } else {
                    totalPrice.amount += price.amount
                    totalPrice.amountCent += price.amount * 100
                }
            })

            return totalPrice
        }

        filterCanceledServices(services) {
            return services
                .filter(
                    service =>
                        service.status.toLowerCase() !== 'canceled' ||
                        (service.status.toLowerCase() === 'canceled' &&
                            (service.salesTerms.find(item => item.type === 'CLIENT').price.amount !== 0 ||
                                service.salesTerms.find(item => item.type === 'SUPPLIER').price.amount !== 0))
                )
                .filter(service => service.status.toLowerCase() !== 'rejected')
        }

        toggleAllServices(val) {
            if (val) {
                this.checkboxServicesSelected = [...this.nonPackageServices, ...this.packageServices].map(
                    ({serviceId}) => serviceId
                )
            } else {
                this.checkboxServicesSelected = []
            }
        }

        get isShowCancelledItineraryMessage() {
            if (!this.services?.length) return false
            return this.services.every(service =>
                ['ERROR', 'CANCELED', 'REJECTED'].includes(runtimeStore.orderServiceStatus(service.status))
            )
        }

        get isShowNoPlansItineraryMessage() {
            return this.isEmptyOrder
        }

        get isALlServicesChecked() {
            return this.checkboxServicesSelected.length === this.nonPackageServices.length
        }

        get orderEndDate() {
            if (!this.order.services?.length) return null

            return [...this.order.services].sort((a, b) => {
                return this.$dateFns.isAfter(new Date(a.endDateTime), new Date(b.endDateTime)) ? -1 : 1
            })[0].endDateTime
        }

        get internalNotes() {
            return this.communications.filter(
                ({communicationType, visibleForClients, visibleForSuppliers, status}) =>
                    communicationType === 'NOTES' &&
                    (this.isTO1 ||
                        (status === 'ACTIVE' &&
                            ((authStore.isSupplier && visibleForSuppliers) ||
                                (!authStore.isSupplier && visibleForClients))))
            )
        }

        get communicationsWithoutInternalNotes() {
            return this.communications.filter(e => e.communicationType !== 'NOTES')
        }

        get daysBeforeTrip() {
            const isAfter = this.$dateFns.isAfter(new Date(this.itineraryDays[0].date), new Date())
            const dayDiff = this.$dateFns.differenceInDays(new Date(this.itineraryDays[0].date), new Date())

            return isAfter ? dayDiff + 1 : dayDiff
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get tabSelectedName() {
            return this.tabs[this.selectedTabIndex]?.name
        }

        get tabs() {
            const tabs = [{icon: 'mdi-format-list-bulleted', name: 'info', label: 'services'}]

            if (this.$conf.public.account.showCommunications && this.isTO1) {
                tabs.push({icon: 'mdi-account-group-outline', name: 'communications', label: 'communications'})
            }

            if (this.$conf.public.account.showCommunications && (this.internalNotes.length || this.isTO1)) {
                tabs.push({icon: 'mdi-note-multiple-outline', name: 'internalNotes', label: 'internal_notes'})
            }

            if (this.isB2B) {
                tabs.push({icon: 'mdi-paperclip', name: 'attachments', label: 'attachments'})
            }

            if (this.isMobile) {
                tabs.push({icon: 'mdi-application-outline', name: 'summary', label: 'summary'})
                if (this.isShowWeather) {
                    tabs.push({icon: 'mdi-weather-partly-cloudy', name: 'weather', label: 'weather'})
                }

                if (this.isShowMap) {
                    tabs.push({icon: 'mdi-map-marker', name: 'map', label: 'map'})
                }

                if (this.$conf.public.account.showTips) {
                    tabs.push({icon: 'mdi-lightbulb-on-outline', name: 'tips', label: 'tips_title'})
                }

                const mobileTabsOrderNames = [
                    'summary',
                    'info',
                    'communications',
                    'internalNotes',
                    'tips',
                    'map',
                    'weather',
                ]

                tabs.sort((a, b) => {
                    if (a.name !== b.name) {
                        const firstIndex = mobileTabsOrderNames.indexOf(a.name)
                        const secondIndex = mobileTabsOrderNames.indexOf(b.name)
                        if (firstIndex === -1) {
                            return 1
                        }
                        if (secondIndex === -1) {
                            return -1
                        }
                        return firstIndex - secondIndex
                    } else {
                        return 0
                    }
                })
            }

            return tabs
        }

        get isB2B() {
            return authStore.isB2B
        }

        get isShowWeather() {
            if (!this.weatherStartDate && !this.weatherEndDate) return false

            const start = this.$dateFns.differenceInCalendarDays(
                this.$dateFns.parseISO(this.weatherStartDate),
                new Date()
            )
            const end = this.$dateFns.differenceInCalendarDays(this.$dateFns.parseISO(this.weatherEndDate), new Date())
            const isWithinInterval = this.$dateFns.isWithinInterval(new Date(), {
                start: new Date(this.weatherStartDate),
                end: new Date(this.weatherEndDate),
            })

            return (start >= -4 && start <= 4) || (end >= 0 && end <= 4) || isWithinInterval
        }

        get servicesCanBeCancelled() {
            return this.sortedServices.filter(s => {
                return (
                    !!s.cancellationAllowed ||
                    (s.status === 'Confirmation pending' && this.$conf.public.account.cancelConfirmationPendingService)
                )
            })
        }

        get servicesQuote() {
            return this.sortedServices.filter(s => {
                return s.status === 'Quote'
            })
        }

        get sortedServices() {
            const sortedServices = !this.showCancelledServices
                ? this.filterCanceledServices(this.getSortedServices(this.order.services))
                : this.getSortedServices(this.order.services)

            return this.servicesOrder.length
                ? sortedServices.sort(
                      (a, b) =>
                          this.servicesOrder.find(({processId}) => processId === a.processId)?.orderNumber -
                          this.servicesOrder.find(({processId}) => processId === b.processId)?.orderNumber
                  )
                : sortedServices
        }

        get packageServices() {
            return this.sortedServices
                .filter(s => !!s.serviceContainerId)
                .filter(s => this.serviceFilterBySelectedVariant(s) && this.serviceFilterBySelectedTraveler(s))
        }

        get nonPackageServices() {
            const nonPackageServices = this.sortedServices
                .filter(s => !s.serviceContainerId)
                .filter(s => this.serviceFilterBySelectedVariant(s) && this.serviceFilterBySelectedTraveler(s))

            if (!this.showCancelledServices) {
                return this.filterCanceledServices(nonPackageServices)
            }

            return nonPackageServices
        }

        serviceFilterBySelectedTraveler(s) {
            return (
                !this.selectedTravelerName ||
                (this.selectedTravelerName &&
                    s.travelers.some(
                        t =>
                            `${t.name[0]?.firstName ? t.name[0].firstName : ''} ${
                                t.name[0].lastName ? t.name[0].lastName : ''
                            }` === this.selectedTravelerName
                    ))
            )
        }

        serviceFilterBySelectedVariant(service) {
            return this.variantFilter?.length
                ? !!this.variantFilter.filter(variant => service.serviceVariants?.includes(variant))?.length ||
                      !service.serviceVariants?.length
                : true
        }

        get order() {
            return runtimeStore.orderDetails
        }

        get isEmptyOrder() {
            return !this.order.services?.length
        }

        get gotMoreTwoServices() {
            return this.order.services?.length >= 2
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isTourOperator() {
            return authStore.isTourOperator
        }

        get isTO1() {
            return authStore.isTO1
        }

        get isShowCheckbox() {
            return this.isTO1
        }

        get isTO2() {
            return authStore.isTO2
        }

        get isCorporate() {
            return authStore.isCorporate
        }

        get personRole() {
            return authStore.personRole
        }

        get tourLead() {
            const tourLead =
                this.order && this.order.services && this.order.services[0].travelers.find(t => t.isTourLead === true)
            return tourLead
                ? `${tourLead.prefix} ${tourLead.name[0].firstName} ${tourLead.name[0].lastName} (${tourLead.citizenshipName})`
                : false
        }

        get itineraryViewServices() {
            const showService = service =>
                    !['ERROR', 'CANCELED', 'REJECTED'].includes(runtimeStore.orderServiceStatus(service.status)),
                buildItineraryForFlights = () => {
                    let segments,
                        rebuildServiceSegments = []
                    this.order.services
                        .filter(s => showService(s))
                        .forEach(service => {
                            if (service.serviceType === 'FLIGHT') {
                                segments = service.serviceDetails[0].itineraries.map(
                                    ({flightSegments}) => flightSegments
                                )
                                segments = segments.flat()
                                rebuildServiceSegments = rebuildServiceSegments.concat(
                                    segments.map((segment, index) => {
                                        return {
                                            ...segment,
                                            ...service,
                                            startDateTime: segment.departureDate,
                                            endDateTime: segment.arrivalDate,
                                            firstFlightSegment: index === 0,
                                        }
                                    })
                                )
                            }
                        })
                    return this.buildItineraryForAccommodation(rebuildServiceSegments)
                }
            if (this.order.services) {
                const services = clone(this.order.services).filter(service => showService(service))
                services.forEach(s => (s.icon = this.getServiceTypeIcon(s)))
                const restServices = this.buildItineraryForAccommodation(
                    services.filter(s => s.serviceType !== 'FLIGHT' && showService(s))
                )
                const dateTimeFlights = buildItineraryForFlights(
                    services.filter(s => s.serviceType === 'FLIGHT' && showService(s))
                )
                const uniqKeys = Array.from(new Set([...Object.keys(restServices), ...Object.keys(dateTimeFlights)]))
                const dirtyResult = {...restServices, ...dateTimeFlights}
                uniqKeys.forEach(key => {
                    if (restServices[key] && dateTimeFlights[key]) {
                        dirtyResult[key].times = dirtyResult[key].times.concat(restServices[key].times)
                    }
                })
                const result = Object.values(dirtyResult)
                result.forEach(date => {
                    date.times.sort((a, b) => {
                        return a.time > b.time ? 1 : -1
                    })
                })
                const sortByDate = (a, b) => {
                    return this.$dateFns.isAfter(new Date(a.date), new Date(b.date)) ? 1 : -1
                }
                return result.sort(sortByDate)
            }
            return []
        }

        get itineraryDays() {
            if (!this.itineraryViewServices.length) return this.orderItinerary?.itineraryDays || []
            const firstDate = this.$dateFns.parseISO(this.itineraryViewServices[0].date)
            if (!this.orderItinerary.itineraryDays?.length) {
                return this.itineraryViewServices.reduce((itineraryViewServices, services, index) => {
                    const date = this.$dateFns.parseISO(services.date)
                    const number =
                        index === 0
                            ? 1
                            : this.$dateFns.differenceInCalendarDays(date, itineraryViewServices[0].date) + 1
                    itineraryViewServices.push({number, services, date})

                    return itineraryViewServices
                }, [])
            }
            return this.orderItinerary.itineraryDays.map((itineraryDay, index) => {
                const date = this.$dateFns.addDays(firstDate, index),
                    services = this.itineraryViewServices.find(services =>
                        this.$dateFns.isSameDay(date, this.$dateFns.parseISO(services.date))
                    )
                return {...itineraryDay, services, date}
            })
        }

        get headers() {
            if (this.isMobile) {
                return [{value: 'mobile', align: 'left'}]
            }

            const headers = [
                {title: '#', value: 'id'},
                {title: this.$t('order_service'), value: 'title'},
                {title: this.$t('dates'), value: 'date', align: 'center'},
                {title: this.$t('order_travelers'), value: 'travellers'},
                {title: this.$t('status'), value: 'status'},
            ]

            if (this.isTourOperator && this.commissionAndNettoCheckbox) {
                headers.push({title: this.$t('supplier_price'), value: 'supplierPrice', align: 'end'})
            }
            if (!this.$conf.public.account.licenseeForCorporateCompany) {
                headers.push({
                    title: this.$t(this.isTourOperator ? 'client_price' : 'price'),
                    value: 'price',
                    align: 'end',
                })
            }

            if (this.showExchangeRates) {
                if (!this.$conf.public.account.paymentScheduler) {
                    headers.push({title: this.$t('bookingXR'), value: 'bookingXR', align: 'end'})
                    headers.push({title: this.$t('paymentXR'), value: 'paymentXR', align: 'end'})
                }
                headers.push({title: this.$t('fxGainLoss'), value: 'fxGainLoss', align: 'end'})
            }

            if (
                !((this.isAgency || this.isTourOperator) && !this.commissionAndNettoCheckbox) &&
                !this.$conf.public.account.licenseeForCorporateCompany
            ) {
                headers.push({title: this.$t('income'), value: 'income', align: 'end'})
            }

            if (this.isShowVoucherBtn) {
                headers.push({title: this.$t('voucher'), value: 'voucher', align: 'center'})
            }

            return headers
        }

        @Provide('preparedServices') get services() {
            const orderServices = []
            const travellersLabel = service => {
                if (!service || !service.travelers || !service.travelers.length) return '-'
                let travellers = `${service.travelers[0].prefix || ''} ${
                    service.travelers[0].name[0].firstName || ''
                } ${service.travelers[0].name[0].lastName || ''}`
                if (service.travelers.length > 1) {
                    travellers += ` (+${service.travelers.length - 1})`
                }
                return travellers
            }
            let sortedServices
            if (!this.showCancelledServices) {
                sortedServices = this.getSortedServices(this.filterCanceledServices(this.order.services))
                    .filter(s => this.serviceFilterBySelectedTraveler(s) && this.serviceFilterBySelectedVariant(s))
                    .filter(s => this.checkboxServicesSelected.includes(s.serviceId))
            } else
                sortedServices = this.getSortedServices(this.order.services)
                    .filter(s => this.serviceFilterBySelectedTraveler(s) && this.serviceFilterBySelectedVariant(s))
                    .filter(s => this.checkboxServicesSelected.includes(s.serviceId))

            // TODO wtf? Need refactoring and remove this in all places
            if (!this.order.services?.length) {
                orderServices.push({
                    orderId: this.order.orderId,
                    statusColor: runtimeStore.orderServiceStatusColor(
                        runtimeStore.orderServiceStatus(this.order.status || 'PENDING')
                    ),
                    isEmptyOrder: true,
                })
            }

            sortedServices.forEach(service => {
                let location = '',
                    pureTitle = `<strong>${service.serviceName}</strong>`,
                    title = '',
                    roomTypeName = null,
                    date = this.$filters.dateShortFormat(service.startDateTime),
                    created = this.$filters.dateShortFormat(service.created),
                    modified = this.$filters.dateShortFormat(this.order.modified),
                    countryName,
                    cityName,
                    travelersMealType,
                    serviceId = service.serviceId
                //const transferPlace = place => place.address || place.airportCode || ''
                switch (service.serviceType) {
                    case 'ACCOMMODATION':
                        travelersMealType = [
                            ...new Set(
                                service.travelers.reduce((mealTypes, traveler) => {
                                    if (traveler.mealType) {
                                        mealTypes.push(traveler.mealType)
                                    }
                                    return mealTypes
                                }, [])
                            ),
                        ].join(', ')
                        service.serviceDetailsContainer.accommodation.rooms.forEach(room => {
                            title += `${room.roomTypeName}, ${travelersMealType || room.mealTypeName}<br>`
                            roomTypeName = `${room.roomTypeName} `
                        })
                        countryName = service.serviceDetailsContainer.accommodation.countryName
                            ? service.serviceDetailsContainer.accommodation.countryName
                            : ''
                        cityName = service.serviceDetailsContainer.accommodation.cityName
                            ? service.serviceDetailsContainer.accommodation.cityName
                            : ''
                        location = []
                        if (countryName) {
                            location.push(countryName)
                        }
                        if (cityName) {
                            location.push(cityName)
                        }
                        location = location.join(', ')
                        date += `${this.isMobile ? ' - ' : '<br>'}` + this.$filters.dateShortFormat(service.endDateTime)
                        break
                    case 'FLIGHT':
                    case 'OWNCHARTER':
                        location = service.serviceDetailsContainer.flight.flightName
                        break
                    case 'ACTIVITY':
                        location = service.serviceDetailsContainer.activity.location
                        if (service.productVariantId === +this.$conf.public.productVariantIds?.multiDayTours) {
                            date +=
                                `${this.isMobile ? ' - ' : '<br>'}` + this.$filters.dateShortFormat(service.endDateTime)
                        }
                        break
                    case 'TRANSFER':
                        break
                    case 'CARRENT':
                        date += `${this.isMobile ? ' - ' : '<br>'}` + this.$filters.dateShortFormat(service.endDateTime)
                        break
                }
                const orderService = {
                    orderId: this.order.orderId,
                    created: created,
                    modified: modified,
                    clientCompanyName: this.order.clientCompanyName,
                    pureTitle,
                    title,
                    ...(roomTypeName && {roomTypeName}),
                    serviceType: service.serviceType,
                    ...(service.productVariantId && {productVariantId: service.productVariantId}),
                    location,
                    date,
                    travellers: travellersLabel(service),
                    //TODO [API] Fix statuses
                    status: this.$t(`bookingStatus.${runtimeStore.orderServiceStatus(service.status)}`),
                    originStatus: service.status,
                    statusColor: runtimeStore.orderServiceStatusColor(runtimeStore.orderServiceStatus(service.status)),
                    supplierCompanyName: service.supplierCompanyName,
                    supplier: {
                        paymentStatus: service.supplierPaymentStatus,
                        cardGuarantee: service.serviceDetailsContainer.accommodation
                            ? service.serviceDetailsContainer.accommodation.cardGuarantee
                            : false,
                        price: service.salesTerms.find(salesTerm => salesTerm.type === 'SUPPLIER')?.price || {
                            amount: 0,
                            currency: persistentStore.currency,
                        },
                        commissionVatAmount: service.salesTerms.find(salesTerm => salesTerm.type === 'SUPPLIER')
                            ?.commissionVatAmount,
                    },
                    customer: {
                        paymentStatus: service.customerPaymentStatus,
                        price: service.salesTerms.find(salesTerm => salesTerm.type === 'CLIENT')?.price || {
                            amount: 0,
                            currency: persistentStore.currency,
                        },
                        commissionVatAmount: service.salesTerms.find(salesTerm => salesTerm.type === 'CLIENT')
                            ?.commissionVatAmount,
                    },
                    violation: service.violation,
                    corporatePolicies: service.corporatePolicies || [],
                    processId: service.processId,
                    serviceName: service.serviceName,
                    serviceId,
                    income: {},
                    ...(service.serviceExchangeRates?.bookingXR && {
                        bookingXR: service.serviceExchangeRates?.bookingXR,
                    }),
                    ...(service.serviceExchangeRates?.serviceCreationXR && {
                        serviceCreationXR: service.serviceExchangeRates?.serviceCreationXR,
                    }),
                    showAllServicesPrice:
                        this.isTO1 || this.order.clientPriceType === 'FOR_ALL_SERVICES' || !this.order.clientPriceType,
                    orderNumber: service.orderNumber,
                    showClientPrice: !service.serviceContainerId || !this.isStaticPackage,
                    visibleInReservation: service.visibleInReservation,
                }

                if (this.showExchangeRates) {
                    orderService.bookingXR = service.serviceExchangeRates?.bookingXR
                        ? service.serviceExchangeRates.bookingXR
                        : 0
                    orderService.paymentXR = service.serviceExchangeRates?.paymentXR
                        ? service.serviceExchangeRates.paymentXR
                        : 0
                    orderService.fxGainLoss = {
                        amount: service.serviceExchangeRates?.fxGainLoss ? service.serviceExchangeRates.fxGainLoss : 0,
                        currency: service.salesTerms.find(salesTerm => salesTerm.type === 'CLIENT').price.currency,
                    }
                }

                // TODO: temporary solution, need fix on the backend
                if (this.isAgency && !orderService.customer.price.commission)
                    orderService.customer.price.commission = orderService.supplier.price.commission

                const convertBruttoPrice = orderService.customer.price
                const convertBruttoCommision = {
                    amount: orderService.customer.price.commission,
                    currency: orderService.customer.price.currency,
                }

                let convertNettoAmountByXr, convertNettoCommisionByXr

                const serviceOriginalXR = orderService.bookingXR || orderService.serviceCreationXR
                if (serviceOriginalXR) {
                    convertNettoAmountByXr = {
                        amount: orderService.supplier.price.amount * serviceOriginalXR,
                        currency: orderService.customer.price.currency,
                    }
                    convertNettoCommisionByXr = {
                        amount: orderService.supplier.price.commission * serviceOriginalXR,
                        currency: orderService.customer.price.currency,
                    }
                } else {
                    convertNettoAmountByXr = convertPrice(
                        orderService.supplier.price,
                        orderService.customer.price.currency
                    )
                    convertNettoCommisionByXr = convertPrice(
                        {
                            amount: orderService.supplier.price.commission,
                            currency: orderService.supplier.price.currency,
                        },
                        orderService.customer.price.currency
                    )
                }

                let income
                if (this.isAgency) {
                    income = convertBruttoCommision.amount
                } else {
                    income =
                        convertBruttoPrice.amount -
                        convertBruttoCommision.amount -
                        (convertNettoAmountByXr.amount - convertNettoCommisionByXr.amount)
                }

                const percent = (income * 100) / Math.abs(convertBruttoPrice.amount)
                orderService.income.amount = income
                orderService.income.currency = convertBruttoPrice.currency
                orderService.income.percent = convertBruttoPrice.amount ? Math.round(percent * 100) / 100 : null

                orderService.income = {...orderService.income}

                orderServices.push(orderService)
            })

            return this.servicesOrder.length
                ? orderServices.sort(
                      (a, b) =>
                          this.servicesOrder.find(({processId}) => processId === a.processId)?.orderNumber -
                          this.servicesOrder.find(({processId}) => processId === b.processId)?.orderNumber
                  )
                : orderServices
        }

        get commissionAndNettoCheckbox() {
            return persistentStore.commissionAndNettoCheckbox
        }

        calculateOrderPrices(type = 'CLIENT') {
            const servicesToCalculate = this.order.services.filter(s =>
                this.checkboxServicesSelected.includes(s.serviceId)
            )

            const servicesExcluded = this.order.services.filter(
                s => !this.checkboxServicesSelected.includes(s.serviceId)
            )

            const orderPricesCalculated = [
                ...new Set(
                    servicesToCalculate.map(s => s.salesTerms.find(op => op.type === type)).map(st => st.price.currency)
                ),
            ].map(currency => {
                const paidAmountOrder = this.order.orderPrices
                    .find(orderPrice => orderPrice.type === type)
                    .orderPrices.find(op => op.currency === currency).paidAmount
                let dueToPayCalculated = this.order.orderPrices
                    .find(orderPrice => orderPrice.type === type)
                    .orderPrices.find(op => op.currency === currency).dueToPay
                let amountCalculated = 0
                let commissionCalculated = 0

                servicesExcluded
                    .filter(s => !!s.salesTerms.find(st => st.type === type && st.price.currency === currency))
                    .map(s => s.salesTerms.find(st => st.type === type && st.price.currency === currency))
                    .forEach(st => {
                        dueToPayCalculated -= st.price.amountDue
                    })

                servicesToCalculate
                    .filter(s => !!s.salesTerms.find(st => st.type === type && st.price.currency === currency))
                    .map(s => s.salesTerms.find(st => st.type === type && st.price.currency === currency))
                    .forEach(st => {
                        amountCalculated += st.price.amount
                        commissionCalculated += st.price.commission
                    })

                return {
                    currency: currency,
                    amount: amountCalculated,
                    commission: commissionCalculated,
                    dueToPay: dueToPayCalculated,
                    paidAmount: paidAmountOrder,
                }
            })

            return orderPricesCalculated
        }

        get nettoPrice() {
            let nettoPrices
            if (this.checkboxServicesSelected?.length === this.order.services?.length) {
                nettoPrices = clone(
                    this.order.orderPrices.find(orderPrice => orderPrice.type === 'SUPPLIER').orderPrices
                )
            } else {
                nettoPrices = this.calculateOrderPrices('SUPPLIER')
            }

            nettoPrices.forEach(nettoPrice => {
                const servicesInCurrency = this.services.filter(
                    service => service.supplier.price.currency === nettoPrice.currency
                )

                nettoPrice.amountInClientCurrency = 0
                nettoPrice.commissionInClientCurrency = 0

                servicesInCurrency.forEach(item => {
                    nettoPrice.currencyClient = item.customer.price.currency

                    const serviceOriginalXR = item.bookingXR || item.serviceCreationXR

                    if (serviceOriginalXR) {
                        nettoPrice.amountInClientCurrency += item.supplier.price.amount * serviceOriginalXR
                        nettoPrice.commissionInClientCurrency +=
                            (item.supplier.price.commission || 0) * serviceOriginalXR
                    } else {
                        const priceConverted = this.$filters.convertPrice(
                            item.supplier.price,
                            nettoPrice.currencyClient
                        )
                        const commissionConverted = this.$filters.convertPrice(
                            {
                                amount: item.supplier.price.commission || 0,
                                currency: item.supplier.price.currency,
                            },
                            nettoPrice.currencyClient
                        )

                        nettoPrice.amountInClientCurrency += priceConverted.amount
                        nettoPrice.commissionInClientCurrency += commissionConverted.amount
                    }
                })

                const averageExchageRate = nettoPrice.amountInClientCurrency / nettoPrice.amount

                nettoPrice.paidAmountInClientCurrency = nettoPrice.paidAmount * averageExchageRate
                nettoPrice.dueToPayInClientCurrency =
                    nettoPrice.amountInClientCurrency - nettoPrice.paidAmountInClientCurrency
            })

            return nettoPrices
        }

        get bruttoPrice() {
            if (this.checkboxServicesSelected?.length === this.order.services?.length) {
                return clone(this.order.orderPrices.find(orderPrice => orderPrice.type === 'CLIENT')?.orderPrices)
            } else {
                return this.calculateOrderPrices('CLIENT')
            }
        }

        get totalNettoPriceInClientCurrency() {
            return this.calculateTotalPriceFromArray(
                this.nettoPrice.map(price => ({
                    amount: price.amountInClientCurrency,
                    currency: price.currencyClient,
                }))
            )
        }

        get totalBruttoPrice() {
            return this.calculateTotalPriceFromArray(this.bruttoPrice)
        }

        get totalBruttoCommission() {
            return this.calculateTotalPriceFromArray(
                this.bruttoPrice.map(e => ({
                    amount: e.commission || 0,
                    currency: e.currency,
                }))
            )
        }

        get totalNettoCommissionInClientCurrency() {
            return this.calculateTotalPriceFromArray(
                this.nettoPrice.map(price => ({
                    amount: price.commissionInClientCurrency,
                    currency: price.currencyClient,
                }))
            )
        }

        get totalIncomeInClientCurrency() {
            if (this.isEmptyOrder) {
                return null
            }

            const totalIncome = this.calculateTotalPriceFromArray(
                this.services.filter(s => this.checkboxServicesSelected.includes(s.serviceId)).map(s => s.income)
            )
            if (!totalIncome) return null

            totalIncome.amount += this.totalFxGainLoss.amount
            totalIncome.amountCent += this.totalFxGainLoss.amountCent

            const incomePercent = (totalIncome.amountCent * 100) / this.totalBruttoPrice.amountCent
            const percentRounded = Math.round(incomePercent * 100) / 100

            return {...totalIncome, percent: percentRounded}
        }

        get totalFxGainLoss() {
            if (!this.$conf.public.account.paymentScheduler) {
                const amount = this.order.services.reduce((total, service) => {
                    total += service.serviceExchangeRates?.fxGainLoss ? service.serviceExchangeRates.fxGainLoss : 0

                    return total
                }, 0)
                const currency = this.order.orderPrices.find(el => el.type === 'CLIENT').orderPrices[0].currency

                return this.calculateTotalPriceFromArray([{amount, currency}])
            } else {
                const amount = this.order.fxGainLoss || 0
                const currency = this.bruttoPrice[0].currency

                return this.calculateTotalPriceFromArray([{amount, currency}])
            }
        }

        get showExchangeRates() {
            return this.$conf.public.showExchangeRates && this.isTO1
        }

        get orderTravelers() {
            return getOrderTravelers(this.order)
        }

        get vatCalculatedAmountByEachServiceBrutto() {
            return this.calculateVatAmountByEachService('CLIENT')
        }

        get vatCalculatedAmountByEachServiceNetto() {
            return this.calculateVatAmountByEachService('SUPPLIER')
        }

        calculateVatAmountByEachService(type) {
            return +this.order.services
                .filter(s => this.checkboxServicesSelected.includes(s.serviceId))
                ?.reduce((sum, service) => {
                    let sumCent = sum * 100
                    const salesTerm = service.salesTerms.find(item => item.type === type)

                    sumCent += salesTerm?.commissionVatAmount ? salesTerm.commissionVatAmount * 100 : 0

                    return sumCent / 100
                }, 0)
                .toFixed(2)
        }

        get nettoDueToPay() {
            return this.nettoPrice
        }

        get bruttoDueToPay() {
            if (this.isAgency) return this.nettoPrice

            return this.bruttoPrice
        }

        get quoteStatusAll() {
            return this.services.every(service => service.originStatus === 'Quote')
        }

        get showPaymentAndInvoiceStatus() {
            return authStore.showPaymentAndInvoiceStatus
        }

        get showNettoPaymentStatus() {
            return true
        }

        get locale() {
            return this.$i18n.locale
        }

        get showCancelledServices() {
            return persistentStore.showCancelledServices
        }

        get isShowSettingsBtn() {
            return this.isAgency || this.isTourOperator || this.isCorporate
        }

        get showSystemNettoCurrency() {
            return false
        }

        get currency() {
            return persistentStore.currency
        }

        get showBookAllBtn() {
            return this.servicesQuote.length > 0 && this.sortedServices.length > 1 && !this.order.orderPackage
        }

        get itineraryView() {
            return persistentStore.isItineraryView
        }

        get packageComponent() {
            return this.order.orderPackage.packageType === 'DYNAMIC_PACKAGE'
                ? resolveComponent('PackageInfo')
                : resolveComponent('PackageTourInfo')
        }

        get variantsAvailable() {
            const variantsArray = this.sortedServices.reduce((variants, service) => {
                return [...variants, ...(service.serviceVariants || [])]
            }, [])

            return [...new Set(variantsArray)]
        }

        get isShowVoucherBtn() {
            return true
        }

        get isShowMap() {
            return this.orderDetailsMapMarkers?.length || this.destinations?.length
        }

        get showAddServiceBtn() {
            return false
        }

        get isShowOrderStatus() {
            return true
        }

        get orderStatus() {
            if (!this.order.status) return null
            return this.$t(`order_statuses.${this.order.status.toUpperCase().replace(/[\s.]/g, '_')}`)
        }

        get showMoveToArchive() {
            return this.$conf.public.showMoveToArchive && (this.isTourOperator || this.isAgency)
        }
    }

    export default OrderDetails
</script>

<style lang="scss" scoped>
    @use 'vuetify/settings';
    @use 'sass:map';

    :deep(.v-slide-group__next--disabled),
    :deep(.v-slide-group__prev--disabled) {
        display: none;
    }

    .commission {
        font-size: 10px;
        line-height: 1rem;
    }

    .display-format-label {
        font-size: 13px;
        color: rgba(0, 0, 0, 0.6);
    }

    @media #{map.get(settings.$display-breakpoints, 'lg-and-up')} {
        .time {
            width: 90px;
        }
    }

    @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
        .order-details__header-title-wrapper {
            order: unset !important;
        }
    }

    .order-wrapper {
        border: 3px solid rgb(52, 150, 243);
        border-radius: 5px;
        margin-bottom: 10px;

        & .services {
            padding: 10px;
        }
    }

    @media (min-width: 991px) {
        .order-summary {
            position: sticky;
            top: 0;
        }
    }

    .view-summary-button {
        position: sticky;
        bottom: 0;
        z-index: 200;
    }

    :deep(.order-summary) .commission-block {
        font-size: 12px !important;
    }

    :deep(.selected-service-modal .service-card) {
        margin-bottom: 0 !important;
    }

    table .th-footer {
        vertical-align: top;
    }

    :deep(.v-data-table.sortable) table tbody tr,
    :deep(.sortable) .service-card > .v-alert {
        cursor: pointer;
    }

    @media (max-width: 960px) {
        :deep(.table) tbody .v-data-table__mobile-row__cell {
            width: 100%;
        }
    }
</style>
